import React, { useState, useEffect, useMemo, useCallback } from 'react';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import { useScenario, useEntityFilter, useTagFilter, useCurrencyFilter, useCalculationType } from '../components/layout/Layout';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert, LinearProgress, Autocomplete, IconButton, Radio, RadioGroup, FormControlLabel, FormControl, Grid, FormHelperText, InputLabel, Select, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import EnhancedDataTable from '../components/EnhancedDataTable';
import SearchableDataTable from '../components/SearchableDataTable';
import api from '../services/api';
import { adjustmentTypeToRouteMap } from '../utils/adjustmentTypeUtils';
import {
  annotateDeferredDrilldowns,
  annotatePivotDrilldowns,
  attachDrilldownLineage,
  canDrillIntoTotalCell,
  getDeferredActivityTypes
} from '../utils/calculationDrilldownUtils';
import '../styles/EntityInputs.css';

const TAX_CALCULATIONS_COLUMNS = [
{ field: 'EntityCode', headerName: 'Entity', minWidth: 120 },
{ field: 'EntityName', headerName: 'Entity Name', minWidth: 180, flex: 1 },
{ field: 'AdjustmentCode', headerName: 'Adjustment Code', minWidth: 140 },
{ field: 'AdjustmentName', headerName: 'Adjustment Name', width: 600 },
{ field: 'Tag', headerName: 'Tag', minWidth: 120 },
{ field: 'LocalCurrencyInput', headerName: 'Local Currency Input', minWidth: 160, type: 'number' },
{ field: 'USDInput', headerName: 'USD Input', minWidth: 120, type: 'number' },
{ field: 'TaxEffectedLocalAmount', headerName: 'Tax-Effected Local Amount', minWidth: 180, type: 'number' },
{ field: 'TaxEffectedUSDAmount', headerName: 'Tax-Effected USD Amount', minWidth: 180, type: 'number' },
{ field: 'AdjustmentSource', headerName: 'Adjustment Source', minWidth: 140 }];


// Column definitions for the selection dialog table - matching AdjustmentInputs style
const getSelectionDialogColumns = (handleEdit, handleDelete) => [
{
  field: 'AdjustmentCode',
  headerName: 'ADJUSTMENT CODE',
  width: 280,
  renderCell: (params) => {
    const displayValue = params.row.AdjustmentName ?
    `${params.value} - ${params.row.AdjustmentName}` :
    params.value;
    return <strong>{displayValue}</strong>;
  }
},
{
  field: 'EntityCode',
  headerName: 'ENTITY CODE',
  width: 120
},
{
  field: 'EntityName',
  headerName: 'ENTITY NAME',
  width: 150,
  renderCell: (params) =>
  <Typography variant="body2">{params.value || ''}</Typography>

},
{
  field: 'ActivityType',
  headerName: 'ACTIVITY TYPE',
  width: 160
},
{
  field: 'Amount',
  headerName: 'AMOUNT',
  width: 130,
  valueFormatter: (params) => {
    if (params.value == null) return '';
    const value = params.value;
    const roundedValue = Math.round(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(roundedValue);
  }
},
{
  field: 'USDAmount',
  headerName: 'USD AMOUNT',
  width: 130,
  renderCell: (params) =>
  <Typography variant="body2">
        {params.value != null ? (() => {
      const roundedValue = Math.round(params.value);
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(roundedValue);
    })() : ''}
      </Typography>

},
{
  field: 'Tag',
  headerName: 'TAG',
  width: 120
},
{
  field: 'SubTag',
  headerName: 'SUB TAG',
  width: 100
},
{
  field: 'AdjustmentType',
  headerName: 'TYPE',
  width: 140
},
{
  field: 'actions',
  headerName: 'ACTIONS',
  width: 100,
  sortable: false,
  renderCell: (params) =>
  <Box>
        <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        handleEdit(params.row);
      }}
      title="Edit Adjustment">

          <img src="/assets/Icons/icon_pencil.svg" alt="Edit" style={{ width: 18, height: 18 }} />
        </IconButton>
        <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        handleDelete(params.row);
      }}
      title="Delete Adjustment">

          <img src="/assets/Icons/icon_trash.svg" alt="Delete" style={{ width: 18, height: 18 }} />
        </IconButton>
      </Box>

}];


// Deferred rollforward table headers
const DEFERRED_ROLLFORWARD_HEADERS = [
'GAAP TO STAT TEMPORARY DIFFERENCES',
'STAT TO TAX TEMPORARY DIFFERENCES',
'TAXABLE INCOME TEMP ADJUSTMENT',
'NET OPERATING LOSS',
'VALUATION ALLOWANCE',
'TAX EFFECTED TEMPORARY DIFFERENCES',
'TAX EFFECTED VALUATION ALLOWANCE'];


// Activity types for deferred rollforward columns
const ACTIVITY_TYPES = [
'BeginningBalance',
'ReturnToProvision',
'DeferredTrueUp',
'PurchaseAccounting',
'AdjBeginningBalance',
'CurrentActivityPL',
'OCIAdjustment',
'DeferredExpAdjustment',
'BalanceSheetAdjustment',
'CustomDeferredExpAdjustment',
'CustomBalanceSheetAdjustment'];

const enableTotalCellDrilldown = (columns, handleCellClick) =>
columns.map((column) => {
  if (!column.renderCell || column.field === 'AdjustmentName') return column;
  const renderCell = column.renderCell;

  return {
    ...column,
    renderCell: (params) => {
      const renderedCell = renderCell(params);
      if (!canDrillIntoTotalCell(params) || !React.isValidElement(renderedCell)) {
        return renderedCell;
      }

      return React.cloneElement(renderedCell, {
        onClick: (event) => {
          event.stopPropagation();
          handleCellClick({ ...params, drilldownKeys: column.drilldownKeys });
        },
        title: 'View contributing adjustment inputs',
        sx: {
          ...(renderedCell.props.sx || {}),
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: '#e3f2fd',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }
        }
      });
    }
  };
});


// Tag and SubTag options - stored on frontend as temporary solution
const TAG_OPTIONS = [
'ORDINARY',
'DISCRETE',
'RTP',
'AUDIT',
'OCI',
'PPA',
'Deferred Only',
'Deferred Only PY',
'Balance Sheet Only',
'Balance Sheet Only PY',
'Valuation Allowance',
'Valuation Allowance PY',
'Uncertain Tax Position',
'Uncertain Tax Position PY'];


const SUBTAG_OPTIONS = [
'Import',
'Auto',
'Manual',
'Workpaper',
'Q1',
'Q2',
'Q3',
'Q4',
'2024',
'2023',
'2022'];


// Utility function to format numbers with parentheses for negatives
const formatNumberWithParentheses = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return value;
  }

  const absValue = Math.abs(numValue);
  const formattedValue = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(absValue);

  return numValue < 0 ? `(${formattedValue})` : formattedValue;
};

// Helper to format numbers with commas for display
const formatNumberWithCommas = (value) => {
  if (!value) return '';
  // Remove any existing commas and non-numeric characters except decimal point and minus sign
  const numericValue = value.toString().replace(/[^\d.-]/g, '');
  // Split into integer and decimal parts
  const parts = numericValue.split('.');
  // Add commas to integer part
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  // Return formatted number
  return parts.join('.');
};

// Helper to remove commas from formatted number
const removeCommas = (value) => {
  if (!value) return '';
  return value.toString().replace(/,/g, '');
};

// Utility function to format rounded numbers with parentheses for negatives
const formatRoundedNumberWithParentheses = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return value;
  }

  const absValue = Math.abs(Math.round(numValue));
  const formattedValue = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(absValue);

  return numValue < 0 ? `(${formattedValue})` : formattedValue;
};

const defaultCalculation = {
  EntityCode: '',
  EntityName: '',
  AdjustmentCode: '',
  AdjustmentName: '',
  Tag: '',
  LocalCurrencyInput: '',
  USDInput: '',
  TaxEffectedLocalAmount: '',
  TaxEffectedUSDAmount: '',
  AdjustmentSource: ''
};

// Helper to get Activity Type options based on adjustment type
const getActivityTypeOptions = (adjustmentType) => {
  // Permanent types that can use CurrentActivityEQ
  const permanentTypes = ['Permanent', 'GS Permanent', 'TI Permanent'];

  // Group 1: Current activity types (PL only, except for permanent types)
  const group1 = [
  'Pretax',
  'Permanent',
  'Payable',
  'GS Permanent',
  'TI Permanent',
  'Tax Adj. Return',
  'Tax Adj. Provision',
  'Tax Adj. RTP'];


  // Group 2: Deferred/balance sheet activity types
  const group2 = [
  'Temporary',
  'GS Temporary',
  'TI Temporary',
  'Tax Effected Temporary',
  'Valuation Allowance',
  'Net Operating Loss',
  'Tax Effected Valuation Allowance'];


  if (!adjustmentType) return [];

  if (group1.includes(adjustmentType)) {
    // Only Permanent, GS Permanent, and TI Permanent can use CurrentActivityEQ
    if (permanentTypes.includes(adjustmentType)) {
      return ['CurrentActivityPL', 'CurrentActivityEQ'];
    }
    // All other group1 types only get CurrentActivityPL
    return ['CurrentActivityPL'];
  }

  if (group2.includes(adjustmentType)) {
    return [
    'BeginningBalance',
    'ReturnToProvision',
    'DeferredTrueUp',
    'PurchaseAccounting',
    'CurrentActivityPL',
    'OCIAdjustment',
    'DeferredExpAdjustment',
    'BalanceSheetAdjustment',
    'CustomDeferredExpAdjustment',
    'CustomBalanceSheetAdjustment'];

  }

  return [];
};

const TaxCalculations = () => {
  // Check library availability on component mount
  const checkLibraryAvailability = () => {
    try {
      if (window.XLSX && window.XLSX.utils) {

        // library available
      }} catch (error) {
      console.error('Error checking XLSX methods:', error);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    try {
      // Check if libraries are loaded
      if (typeof window.XLSX === 'undefined' || typeof window.saveAs === 'undefined') {
        console.error('Libraries missing - XLSX:', typeof window.XLSX, 'saveAs:', typeof window.saveAs);
        setNotification({
          open: true,
          message: 'Excel export is not available. Please ensure XLSX and FileSaver are loaded.',
          severity: 'warning'
        });
        return;
      }

      // Get the formatted data that matches what's displayed in the table
      let dataToExport = selectedRows.length > 0 ? rows.filter((row) => selectedRows.includes(row.id)) : rows;

      // Transform the pivot table data into a more Excel-friendly format
      const excelData = dataToExport.map((row) => {
        const excelRow = {};

        // Handle section headers and totals
        if (row.isSection) {
          excelRow['Type'] = 'SECTION HEADER';
          excelRow['Description'] = row.AdjustmentName;
          return excelRow;
        } else if (row.isSubFooter) {
          excelRow['Type'] = 'SECTION TOTAL';
          excelRow['Description'] = row.AdjustmentName;
        } else if (row.isCalculatedTotal) {
          excelRow['Type'] = 'CALCULATED TOTAL';
          excelRow['Description'] = row.AdjustmentName;
        } else {
          excelRow['Type'] = 'ADJUSTMENT';
          excelRow['Adjustment Code'] = row.AdjustmentCode || '';
          excelRow['Description'] = row.AdjustmentName || '';
        }

        // Add entity columns (dynamically based on what entities are present)
        Object.keys(row).forEach((key) => {
          // Skip internal properties and already handled fields
          if (!key.startsWith('is') && key !== 'id' && key !== 'AdjustmentCode' && key !== 'AdjustmentName') {
            // Check if this looks like an entity code (numeric or specific pattern)
            if (key.match(/^\d+$/) || key.length <= 10) {
              excelRow[key] = row[key];
            }
          }
        });

        return excelRow;
      });

      if (excelData.length === 0) {

        setNotification({ open: true, message: 'No data to export', severity: 'warning' });
        return;
      }

      const ws = window.XLSX.utils.json_to_sheet(excelData);
      const wb = window.XLSX.utils.book_new();
      window.XLSX.utils.book_append_sheet(wb, ws, 'TaxCalculations');
      const wbout = window.XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      window.saveAs(blob, 'tax_calculations.xlsx');
      setNotification({ open: true, message: 'Export successful', severity: 'success' });
    } catch (error) {
      console.error('=== Excel Export Debug End - ERROR ===');
      console.error('Export error details:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      setNotification({
        open: true,
        message: `Failed to export tax calculations: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const { selectedScenario } = useScenario();
  const { selectedEntity } = useEntityFilter();
  const { selectedTag } = useTagFilter();
  const { selectedCurrency } = useCurrencyFilter();
  const { calculationsCalculationType: calculationType } = useCalculationType();

  const [allRows, setAllRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityGroups, setEntityGroups] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRow, setCurrentRow] = useState(defaultCalculation);
  const [currentAdjustment, setCurrentAdjustment] = useState({
    id: '',
    AdjustmentId: '',
    EntityCode: '',
    EntityName: '',
    SubEntity: '',
    AdjustmentCode: '',
    AdjustmentName: '',
    ActivityType: '',
    Amount: '',
    USDAmount: '',
    Tag: '',
    SubTag: '',
    AdjustmentType: '',
    ScenarioCode: '',
    PointInTime: '',
    ModifiedBy: 'admin'
  });
  const [entityName, setEntityName] = useState('');
  const [adjustmentName, setAdjustmentName] = useState('');
  const [pointInTimes, setPointInTimes] = useState([]);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [selectedRows, setSelectedRows] = useState([]);
  const [masterEntities, setMasterEntities] = useState([]);
  const [masterAdjustments, setMasterAdjustments] = useState([]);
  const [entityInputs, setEntityInputs] = useState([]);
  const [entityLoading, setEntityLoading] = useState(false);
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  const [entityInputLoading, setEntityInputLoading] = useState(false);
  const [selectionDialogOpen, setSelectionDialogOpen] = useState(false);
  const [matchingAdjustments, setMatchingAdjustments] = useState([]);

  // Fetch master entities
  const fetchMasterEntities = async () => {
    setEntityLoading(true);
    try {
      const response = await api.get('/entity-models/master');
      if (response.data && response.data.data) {
        setMasterEntities(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching master entities:', error);
      setNotification({ open: true, message: 'Failed to fetch entities', severity: 'error' });
    } finally {
      setEntityLoading(false);
    }
  };

  // Fetch entity groups
  const fetchEntityGroups = async () => {
    try {
      const response = await api.get('/entity-groups');
      if (response.data && response.data.data) {
        setEntityGroups(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching entity groups:', error);
    }
  };

  // Fetch master adjustments (for lookup purposes)
  const fetchMasterAdjustments = async () => {
    setAdjustmentLoading(true);
    try {
      const response = await api.get('/adjustments/master');
      if (response.data && response.data.data) {
        setMasterAdjustments(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching master adjustments:', error);
      setNotification({ open: true, message: 'Failed to fetch master adjustments', severity: 'error' });
    } finally {
      setAdjustmentLoading(false);
    }
  };

  // Helper function to get tax rate for an entity from EntityInput
  const getTaxRateForEntity = (entityCode, scenarioCode = null) => {
    const entityInput = entityInputs.find((ei) =>
    ei.EntityCode === entityCode && (
    !scenarioCode || ei.ScenarioCode === scenarioCode)
    );

    // Get CurrentTaxRate from EntityInput, default to 0 if not found
    const taxRate = entityInput?.CurrentTaxRate || 0;
    // Convert to decimal if it's stored as percentage (>1)
    return taxRate > 1 ? taxRate / 100 : taxRate;
  };

  // Helper function to get tax rate for a specific activity type in deferred rollforward
  const getTaxRateForActivityType = (entityCode, activityType, scenarioCode = null) => {
    const entityInput = entityInputs.find((ei) =>
    ei.EntityCode === entityCode && (
    !scenarioCode || ei.ScenarioCode === scenarioCode)
    );

    if (!entityInput) return 0;

    let taxRate = 0;

    // Apply specific tax rate based on activity type
    switch (activityType) {
      case 'BeginningBalance':
        // Beginning balance uses begin deferred tax rate
        taxRate = entityInput.BeginningDeferredTaxRate || 0;
        break;

      case 'ReturnToProvision':
        // Return to provision uses prior year tax rate, or begin deferred if no prior year
        taxRate = entityInput.PriorCurrentTaxRate || entityInput.BeginningDeferredTaxRate || 0;
        break;

      case 'DeferredTrueUp':
      case 'PurchaseAccounting':
        // Deferred true up and purchase accounting use begin deferred tax rate
        taxRate = entityInput.BeginningDeferredTaxRate || 0;
        break;

      case 'CurrentActivityPL':
        // Current activity uses current tax rate
        taxRate = entityInput.CurrentTaxRate || 0;
        break;

      case 'OCIAdjustment':
      case 'DeferredExpAdjustment':
      case 'BalanceSheetAdjustment':
      case 'CustomDeferredExpAdjustment':
      case 'CustomBalanceSheetAdjustment':
        // All other activity types use end deferred tax rate
        taxRate = entityInput.EndingDeferredTaxRate || 0;
        break;

      default:
        // Default to 0 for unknown activity types or AdjBeginningBalance (which is a total)
        taxRate = 0;
        break;
    }

    // Convert to decimal if it's stored as percentage (>1)
    return taxRate > 1 ? taxRate / 100 : taxRate;
  };

  // Get FX rate for an entity from EntityInput data based on activity type
  const getFXRateForEntity = (entityCode, activityType) => {
    if (!entityCode) return null;
    const entityInput = entityInputs.find((ei) =>
    ei.EntityCode === entityCode && ei.ScenarioCode === selectedScenario?.scenarioCode
    );
    if (!entityInput) return null;

    let fxRate = null;

    // Determine which rate to use based on activity type
    // Beginning Balance and Return to Provision use Beginning Spot Rate
    if (activityType === 'BeginningBalance' || activityType === 'ReturnToProvision') {
      fxRate = entityInput.BeginningSpotRate;
    }
    // Current Activity PL and EQ use Average FX Rate
    else if (activityType === 'CurrentActivityPL' || activityType === 'CurrentActivityEQ') {
      fxRate = entityInput.AverageFXRate;
    }
    // All other activity types use Ending Spot Rate
    else {
      fxRate = entityInput.EndingSpotRate;
    }

    if (!fxRate) return null;

    const parsedRate = parseFloat(fxRate);
    return Number.isFinite(parsedRate) ? parsedRate : null;
  };

  // Handle local currency amount change with automatic USD conversion
  const handleAmountChange = (value) => {
    // Remove commas for calculation
    const numericValue = removeCommas(value);
    const fxRate = getFXRateForEntity(currentAdjustment.EntityCode, currentAdjustment.ActivityType);
    let usdAmount = '';

    // Skip automatic conversion for pretax adjustments
    const isPretax = currentAdjustment.AdjustmentType === 'Pretax' || currentAdjustment.AdjustmentType === 'PretaxAdjustment';

    if (numericValue && fxRate && !isPretax) {
      // USD = Local / FX Rate
      usdAmount = (parseFloat(numericValue) / fxRate).toFixed(2);
    }

    setCurrentAdjustment({
      ...currentAdjustment,
      Amount: numericValue,
      USDAmount: isPretax ? currentAdjustment.USDAmount : usdAmount
    });
  };

  // Handle USD amount change with automatic local currency conversion
  const handleUSDAmountChange = (value) => {
    // Remove commas for calculation
    const numericValue = removeCommas(value);
    const fxRate = getFXRateForEntity(currentAdjustment.EntityCode, currentAdjustment.ActivityType);
    let localAmount = '';

    // Skip automatic conversion for pretax adjustments
    const isPretax = currentAdjustment.AdjustmentType === 'Pretax' || currentAdjustment.AdjustmentType === 'PretaxAdjustment';

    if (numericValue && fxRate && !isPretax) {
      // Local = USD * FX Rate
      localAmount = (parseFloat(numericValue) * fxRate).toFixed(2);
    }

    setCurrentAdjustment({
      ...currentAdjustment,
      Amount: isPretax ? currentAdjustment.Amount : localAmount,
      USDAmount: numericValue
    });
  };

  // Fetch entity inputs (for current tax rates)
  const fetchEntityInputs = async () => {
    if (!selectedScenario) {
      return;
    }

    setEntityInputLoading(true);
    try {
      const response = await api.get('/entity-models/input', {
        params: { scenarioCode: selectedScenario.scenarioCode }
      });
      if (response.data) {
        const data = Array.isArray(response.data) ? response.data : response.data.data || [];
        setEntityInputs(data);
      }
    } catch (error) {
      console.error('Error fetching entity inputs:', error);
      setNotification({ open: true, message: 'Failed to fetch entity inputs', severity: 'error' });
    } finally {
      setEntityInputLoading(false);
    }
  };

  // Fetch tax calculations data (from adjustment inputs)
  const fetchTaxCalculations = async () => {
    if (!selectedScenario) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const scenarioCode = selectedScenario.scenarioCode;

      // Single batched request replaces ~30 per-type round trips.
      const response = await api.get('/adjustments/all', {
        params: { scenarioCode }
      });
      const responseData = response.data;
      const fetchedAdjustments = Array.isArray(responseData) ?
      responseData :
      responseData.data || [];

      // Build O(1) lookup maps once instead of O(n) find() per row.
      const entityByCode = new Map(masterEntities.map((e) => [e.EntityCode, e]));
      const adjustmentByCode = new Map(masterAdjustments.map((a) => [a.AdjustmentCode, a]));

      // Transform to tax calculations format with entity and adjustment name lookups
      const calculationsData = fetchedAdjustments.map((adj) => {
        const entity = entityByCode.get(adj.EntityCode);
        const adjustment = adjustmentByCode.get(adj.AdjustmentCode);

        return {
          id: `${adj.AdjustmentType}_${adj.id}`,
          originalId: adj.id, // Preserve the original backend ID for updates
          AdjustmentType: adj.AdjustmentType, // Preserve adjustment type for routing
          EntityCode: adj.EntityCode || '',
          EntityName: entity?.EntityName || '',
          AdjustmentCode: adj.AdjustmentCode || '',
          AdjustmentName: adjustment?.AdjustmentName || '',
          LocalCurrencyInput: parseFloat(adj.Amount) || 0, // LC amount from backend
          USDInput: parseFloat(adj.USDAmount) || 0, // USD amount from backend
          TaxEffectedLocalAmount: (parseFloat(adj.Amount) || 0) * getTaxRateForEntity(adj.EntityCode, adj.ScenarioCode), // Dynamic tax rate on LC
          TaxEffectedUSDAmount: (parseFloat(adj.USDAmount) || 0) * getTaxRateForEntity(adj.EntityCode, adj.ScenarioCode), // Dynamic tax rate on USD
          AdjustmentSource: adj.AdjustmentType || 'Manual',
          ScenarioCode: adj.ScenarioCode || '',
          Tag: adj.Tag || '',
          SubTag: adj.SubTag || '', // Preserve SubTag from original data
          ActivityType: adj.ActivityType || '', // Preserve ActivityType from original data
          PointInTime: adj.PointInTime || '', // Preserve PointInTime from original data
          // Preserve all original fields for backend updates
          Amount: adj.Amount,
          USDAmount: adj.USDAmount,
          SubEntity: adj.SubEntity,
          ModifiedBy: adj.ModifiedBy || 'admin',
          AdjustmentId: adj.AdjustmentId
        };
      });

      setAllRows(calculationsData);
      setRows(calculationsData);
    } catch (error) {
      console.error('Error fetching tax calculations:', error);
      setNotification({ open: true, message: 'Failed to fetch tax calculations', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      checkLibraryAvailability();

      // Fetch master data in parallel
      await Promise.all([
      fetchMasterEntities(),
      fetchEntityGroups(),
      fetchMasterAdjustments()]
      );
    };

    initializeData();
  }, []);

  useEffect(() => {
    const loadScenarioData = async () => {
      if (!selectedScenario) {
        setLoading(false);
        return;
      }

      setLoading(true);
      await fetchEntityInputs();
    };

    loadScenarioData();
  }, [selectedScenario]);

  useEffect(() => {
    if (masterEntities.length > 0 && masterAdjustments.length > 0) {
      fetchTaxCalculations();
    } else if (!selectedScenario) {
      setLoading(false);
    }
  }, [masterEntities, masterAdjustments, entityInputs]);

  // Section mapping for adjustment types
  const sectionMapping = {
    'Pretax': 'PRETAX BOOK INCOME',
    'GS Permanent': 'GAAP TO STAT PERMANENT DIFFERENCES',
    'GS Temporary': 'GAAP TO STAT TEMPORARY DIFFERENCES',
    'Permanent': 'STAT TO TAX PERMANENT DIFFERENCES',
    'Payable': 'DEDUCTION FOR STATE AND LOCAL TAXES',
    'Temporary': 'STAT TO TAX TEMPORARY DIFFERENCES',
    'TI Permanent': 'TAXABLE INCOME PERM ADJUSTMENT',
    'TI Temporary': 'TAXABLE INCOME TEMP ADJUSTMENT',
    'Net Operating Loss': 'NET OPERATING LOSS',
    'Tax Effected Temporary': 'TAX EFFECTED TEMPORARY DIFFERENCES',
    'Tax Adj. Return': 'RETURN BASIS TAX ADJUSTMENTS',
    'Tax Adj. Provision': 'PROVISION BASIS TAX ADJUSTMENTS',
    'Tax Adj. RTP': 'RETURN TO PROVISION TAX ADJUSTMENTS'
  };

  // Section total titles - customize each section's total label here
  const sectionTotalTitles = {
    'Pretax': 'GAAP PRETAX INCOME',
    'GS Permanent': 'Total GAAP TO STAT PERMANENT DIFFERENCES',
    'GS Temporary': 'Total GAAP TO STAT TEMPORARY DIFFERENCES',
    'Permanent': 'Total STAT TO TAX PERMANENT DIFFERENCES',
    'Payable': 'TOTAL DEDUCTION FOR STATE AND LOCAL TAXES',
    'Temporary': 'Total STAT TO TAX TEMPORARY DIFFERENCES',
    'TI Permanent': 'TOTAL TAXABLE INCOME PERM ADJUSTMENT',
    'TI Temporary': 'TOTAL TAXABLE INCOME TEMP ADJUSTMENT',
    'Net Operating Loss': 'TOTAL NET OPERATING LOSS',
    'Tax Effected Temporary': 'TOTAL TAX EFFECTED TEMPORARY DIFFERENCES',
    'Tax Adj. Return': 'TOTAL RETURN BASIS TAX ADJUSTMENTS',
    'Tax Adj. Provision': 'TOTAL PROVISION BASIS TAX ADJUSTMENTS',
    'Tax Adj. RTP': 'TOTAL RETURN TO PROVISION TAX ADJUSTMENTS'
  };

  // Create deferred rollforward table structure
  const createDeferredRollforwardData = (data) => {
    const deferredRows = [];

    // Get unique entities from the data
    const uniqueEntities = [...new Set(data.map((row) => row.EntityCode))].
    filter((code) => code).
    sort();

    // Mapping of adjustment types to deferred rollforward categories
    const categoryMapping = {
      'GS Temporary': 'GAAP TO STAT TEMPORARY DIFFERENCES',
      'Temporary': 'STAT TO TAX TEMPORARY DIFFERENCES',
      'TI Temporary': 'TAXABLE INCOME TEMP ADJUSTMENT',
      'Net Operating Loss': 'NET OPERATING LOSS',
      'Valuation Allowance': 'VALUATION ALLOWANCE',
      'Tax Effected Temporary': 'TAX EFFECTED TEMPORARY DIFFERENCES',
      'Tax Effected Valuation Allowance': 'TAX EFFECTED VALUATION ALLOWANCE'
    };

    // Create rows for each entity and category combination
    uniqueEntities.forEach((entityCode) => {
      DEFERRED_ROLLFORWARD_HEADERS.forEach((header, headerIndex) => {
        // Find corresponding adjustment type for this category
        const adjustmentType = Object.keys(categoryMapping).find((key) =>
        categoryMapping[key] === header
        );

        if (adjustmentType) {
          // Get adjustments for this category and entity
          const categoryData = data.filter((row) =>
          (row.AdjustmentSource === adjustmentType || row.AdjustmentType === adjustmentType) &&
          row.EntityCode === entityCode
          );

          // Only create section if there is data for this category and entity
          if (categoryData.length === 0) {
            return; // Skip this section if no data
          }

          // Add section header with entity code
          const sectionHeaderRow = {
            id: `section-${entityCode}-${headerIndex}`,
            EntityCode: '',
            AdjustmentCode: '',
            AdjustmentName: `${entityCode} - ${header}`,
            isSection: true
          };
          ACTIVITY_TYPES.forEach((activityType) => {
            sectionHeaderRow[activityType] = null;
            sectionHeaderRow[`${activityType}_TaxEffected`] = null;
          });
          deferredRows.push(sectionHeaderRow);

          // Get unique adjustments in this category for this entity (group by AdjustmentCode)
          const uniqueAdjustments = [...new Set(categoryData.map((row) => row.AdjustmentCode))].
          filter((code) => code).
          sort();

          // Add adjustment rows for this category and entity
          uniqueAdjustments.forEach((adjustmentCode) => {
            const adjustmentData = categoryData.find((row) =>
            row.AdjustmentCode === adjustmentCode && row.EntityCode === entityCode
            );

            if (adjustmentData) {
              const adjustmentName = adjustmentData.AdjustmentName || '';

              const adjustmentRow = {
                id: `${adjustmentType}-${entityCode}-${adjustmentCode}`,
                EntityCode: entityCode,
                AdjustmentCode: adjustmentCode,
                AdjustmentName: adjustmentName,
                AdjustmentType: adjustmentType, // Add adjustment type for cell rendering logic
                isSection: false
              };

              // Initialize all activity type columns to 0 (both regular and tax effected)
              ACTIVITY_TYPES.forEach((activityType) => {
                adjustmentRow[activityType] = 0;
                adjustmentRow[`${activityType}_TaxEffected`] = 0;
              });

              // Find all adjustments with this entity/adjustment combination and populate based on ActivityType
              // Each unique adjustment (by id or composite key) should only be counted once
              const allMatchingAdjustments = categoryData.filter((row) =>
              row.AdjustmentCode === adjustmentCode && row.EntityCode === entityCode
              );

              allMatchingAdjustments.forEach((adj) => {
                const activityType = adj.ActivityType;
                if (activityType && ACTIVITY_TYPES.includes(activityType)) {
                  const amount = selectedCurrency === 'USD' ?
                  parseFloat(adj.USDInput) || parseFloat(adj.USDAmount) || 0 :
                  parseFloat(adj.LocalCurrencyInput) || parseFloat(adj.Amount) || 0;

                  // Always store LC amount for CTA calculations (CTA must use LC values regardless of display currency)
                  const lcAmount = parseFloat(adj.LocalCurrencyInput) || parseFloat(adj.Amount) || 0;

                  // For TAX EFFECTED TEMPORARY DIFFERENCES and TAX EFFECTED VALUATION ALLOWANCE sections, keep (pretax) columns blank
                  // Regular VALUATION ALLOWANCE section should show pretax values
                  if (adjustmentType !== 'Tax Effected Temporary' && adjustmentType !== 'Tax Effected Valuation Allowance') {
                    adjustmentRow[activityType] += amount;
                    // Store LC values separately for CTA calculation
                    if (!adjustmentRow[`${activityType}_LC`]) adjustmentRow[`${activityType}_LC`] = 0;
                    adjustmentRow[`${activityType}_LC`] += lcAmount;
                  }

                  // Calculate tax effected amount
                  let taxEffectedAmount;
                  let taxEffectedLCAmount;
                  if (adjustmentType === 'Tax Effected Temporary' || adjustmentType === 'Tax Effected Valuation Allowance') {
                    // For tax-effected types, the amount is already tax-effected, so use it directly
                    taxEffectedAmount = amount;
                    taxEffectedLCAmount = lcAmount;
                  } else {
                    // For other types (including regular Valuation Allowance), apply activity-specific tax rate
                    const activityTaxRate = getTaxRateForActivityType(entityCode, activityType, selectedScenario?.scenarioCode);
                    taxEffectedAmount = amount * activityTaxRate;
                    taxEffectedLCAmount = lcAmount * activityTaxRate;
                  }
                  adjustmentRow[`${activityType}_TaxEffected`] += taxEffectedAmount;
                  // Store LC tax-effected values separately for CTA calculation
                  if (!adjustmentRow[`${activityType}_TaxEffected_LC`]) adjustmentRow[`${activityType}_TaxEffected_LC`] = 0;
                  adjustmentRow[`${activityType}_TaxEffected_LC`] += taxEffectedLCAmount;
                }
              });

              // Calculate Adj. Beginning Balance (sum of Beginning Balance + Return to Provision + Deferred True-Up + Purchase Accounting)
              const beginningBalance = adjustmentRow['BeginningBalance'] || 0;
              const returnToProvision = adjustmentRow['ReturnToProvision'] || 0;
              const deferredTrueUp = adjustmentRow['DeferredTrueUp'] || 0;
              const purchaseAccounting = adjustmentRow['PurchaseAccounting'] || 0;

              // Calculate Adj. Beginning Balance (pretax)
              adjustmentRow['AdjBeginningBalance'] = beginningBalance + returnToProvision + deferredTrueUp + purchaseAccounting;

              // Calculate Adj. Beginning Balance (tax effected) - sum of tax effected components
              // Note: Each component already has the correct tax rate applied in the loop above
              adjustmentRow['AdjBeginningBalance_TaxEffected'] =
              (adjustmentRow['BeginningBalance_TaxEffected'] || 0) + (
              adjustmentRow['ReturnToProvision_TaxEffected'] || 0) + (
              adjustmentRow['DeferredTrueUp_TaxEffected'] || 0) + (
              adjustmentRow['PurchaseAccounting_TaxEffected'] || 0);

              // Calculate Adj. Beginning Balance using LC values for CTA calculation
              const beginningBalanceLC = adjustmentRow['BeginningBalance_LC'] || 0;
              const returnToProvisionLC = adjustmentRow['ReturnToProvision_LC'] || 0;
              const deferredTrueUpLC = adjustmentRow['DeferredTrueUp_LC'] || 0;
              const purchaseAccountingLC = adjustmentRow['PurchaseAccounting_LC'] || 0;
              const adjBeginningBalanceLC = beginningBalanceLC + returnToProvisionLC + deferredTrueUpLC + purchaseAccountingLC;

              // Calculate Adj. Beginning Balance (tax effected) using LC values for CTA calculation
              const adjBeginningBalanceTaxEffectedLC =
              (adjustmentRow['BeginningBalance_TaxEffected_LC'] || 0) + (
              adjustmentRow['ReturnToProvision_TaxEffected_LC'] || 0) + (
              adjustmentRow['DeferredTrueUp_TaxEffected_LC'] || 0) + (
              adjustmentRow['PurchaseAccounting_TaxEffected_LC'] || 0);

              // Calculate CTA Adjustment using the specified formula
              // CTA Adjustment (Pretax) = [(Beginning Balance (pretax)) x (End Spot Rate – Begin Spot Rate)] + [(Current Activity (pretax)) x (End Spot Rate – Average FX Rate)]
              // CTA Adjustment (Tax Effected) = [(Beginning Balance (tax effected)) x (End Spot Rate – Begin Spot Rate)] + [(Current Activity (tax effected)) x (End Spot Rate – Average FX Rate)]
              // NOTE: CTA calculations ALWAYS use LC values from adjustment inputs, regardless of display currency

              // Get FX rates from EntityInput data for this entity
              const entityInput = entityInputs.find(
                (ei) => ei.EntityCode === entityCode && ei.ScenarioCode === selectedScenario?.scenarioCode
              );
              const toIndirectFxRate = (rate) => {
                if (rate == null || rate === '') return 1.0;
                const parsed = parseFloat(rate);
                if (!Number.isFinite(parsed) || parsed === 0) return 0;
                return 1 / parsed;
              };

              const beginSpotRate = toIndirectFxRate(entityInput?.BeginningSpotRate || 1.0);
              const endSpotRate = toIndirectFxRate(entityInput?.EndingSpotRate || 1.0);
              const averageFXRate = toIndirectFxRate(entityInput?.AverageFXRate || 1.0);

              // Calculate Pretax CTA Adjustment using LC values
              // Beginning balance for pretax = Adj. Beginning Balance (pretax) in LC
              const pretaxBeginningBalanceLC = adjBeginningBalanceLC;

              // Current activity for pretax = all columns between Adj. Beginning Balance and Ending Balance (excluding CTA) in LC
              const pretaxCurrentActivityLC =
              (adjustmentRow['CurrentActivityPL_LC'] || 0) + (
              adjustmentRow['OCIAdjustment_LC'] || 0) + (
              adjustmentRow['DeferredExpAdjustment_LC'] || 0) + (
              adjustmentRow['BalanceSheetAdjustment_LC'] || 0) + (
              adjustmentRow['CustomDeferredExpAdjustment_LC'] || 0) + (
              adjustmentRow['CustomBalanceSheetAdjustment_LC'] || 0);

              const pretaxBeginningComponent = pretaxBeginningBalanceLC * (endSpotRate - beginSpotRate);
              const pretaxCurrentComponent = pretaxCurrentActivityLC * (endSpotRate - averageFXRate);
              const ctaAdjustmentPretax = pretaxBeginningComponent + pretaxCurrentComponent;

              // Zero out CTA adjustment when LC currency is selected
              adjustmentRow['CTAAdjustment'] = selectedCurrency === 'LC' ? 0 : ctaAdjustmentPretax;

              // Calculate Tax-Effected CTA Adjustment using LC values
              // Beginning balance for tax-effected = Adj. Beginning Balance (tax effected) in LC
              const taxEffectedBeginningBalanceLC = adjBeginningBalanceTaxEffectedLC;

              // Current activity for tax-effected = all tax-effected columns between Adj. Beginning Balance and Ending Balance (excluding CTA) in LC
              const taxEffectedCurrentActivityLC =
              (adjustmentRow['CurrentActivityPL_TaxEffected_LC'] || 0) + (
              adjustmentRow['OCIAdjustment_TaxEffected_LC'] || 0) + (
              adjustmentRow['DeferredExpAdjustment_TaxEffected_LC'] || 0) + (
              adjustmentRow['BalanceSheetAdjustment_TaxEffected_LC'] || 0) + (
              adjustmentRow['CustomDeferredExpAdjustment_TaxEffected_LC'] || 0) + (
              adjustmentRow['CustomBalanceSheetAdjustment_TaxEffected_LC'] || 0);

              const taxEffectedBeginningComponent = taxEffectedBeginningBalanceLC * (endSpotRate - beginSpotRate);
              const taxEffectedCurrentComponent = taxEffectedCurrentActivityLC * (endSpotRate - averageFXRate);
              const ctaAdjustmentTaxEffected = taxEffectedBeginningComponent + taxEffectedCurrentComponent;

              // Zero out CTA adjustment when LC currency is selected
              adjustmentRow['CTAAdjustment_TaxEffected'] = selectedCurrency === 'LC' ? 0 : ctaAdjustmentTaxEffected;

              // Calculate Ending Balance: Adj. Beginning Balance + all columns between Adj. Beginning Balance and Ending Balance
              // Columns after AdjBeginningBalance: CurrentActivityPL, OCIAdjustment, DeferredExpAdjustment, BalanceSheetAdjustment, CustomDeferredExpAdjustment, CustomBalanceSheetAdjustment, CTAAdjustment

              // Pretax Ending Balance
              const endingBalancePretax =
              (adjustmentRow['AdjBeginningBalance'] || 0) + (
              adjustmentRow['CurrentActivityPL'] || 0) + (
              adjustmentRow['OCIAdjustment'] || 0) + (
              adjustmentRow['DeferredExpAdjustment'] || 0) + (
              adjustmentRow['BalanceSheetAdjustment'] || 0) + (
              adjustmentRow['CustomDeferredExpAdjustment'] || 0) + (
              adjustmentRow['CustomBalanceSheetAdjustment'] || 0) + (
              adjustmentRow['CTAAdjustment'] || 0);

              // Tax Effected Ending Balance
              const endingBalanceTaxEffected =
              (adjustmentRow['AdjBeginningBalance_TaxEffected'] || 0) + (
              adjustmentRow['CurrentActivityPL_TaxEffected'] || 0) + (
              adjustmentRow['OCIAdjustment_TaxEffected'] || 0) + (
              adjustmentRow['DeferredExpAdjustment_TaxEffected'] || 0) + (
              adjustmentRow['BalanceSheetAdjustment_TaxEffected'] || 0) + (
              adjustmentRow['CustomDeferredExpAdjustment_TaxEffected'] || 0) + (
              adjustmentRow['CustomBalanceSheetAdjustment_TaxEffected'] || 0) + (
              adjustmentRow['CTAAdjustment_TaxEffected'] || 0);

              adjustmentRow['EndingBalance'] = endingBalancePretax;
              adjustmentRow['EndingBalance_TaxEffected'] = endingBalanceTaxEffected;

              // Calculate Tax Rate Change using the specified formula
              // Tax Rate Change = [(pretax Beginning balance + pretax deferred true up + pretax purchase accounting) x (Ending deferred rate - Beginning deferred rate)] 
              //                 + [pretax return to provision x (Ending deferred rate – Prior current rate)]
              //                 + [pretax Current year activity x (Ending deferred rate - Current rate)]  
              //                 + [(pretax deferred exp adjustment + pretax balance sheet adjustment + pretax OCI + pretax custom deferred adjustment + pretax custom balance sheet only adjustment) x (Ending deferred rate - Current rate)]

              // Get tax rates from EntityInput data for this entity
              const entityInputForTaxRate = entityInputs.find(
                (ei) => ei.EntityCode === entityCode && ei.ScenarioCode === selectedScenario?.scenarioCode
              );

              // Get deferred and current tax rates (convert from percentage if needed)
              let endingDeferredRate = entityInputForTaxRate?.EndingDeferredTaxRate || 0;
              endingDeferredRate = endingDeferredRate > 1 ? endingDeferredRate / 100 : endingDeferredRate;

              let beginningDeferredRate = entityInputForTaxRate?.BeginningDeferredTaxRate || 0;
              beginningDeferredRate = beginningDeferredRate > 1 ? beginningDeferredRate / 100 : beginningDeferredRate;

              let priorCurrentRate = entityInputForTaxRate?.PriorCurrentTaxRate || 0;
              priorCurrentRate = priorCurrentRate > 1 ? priorCurrentRate / 100 : priorCurrentRate;

              let currentRate = entityInputForTaxRate?.CurrentTaxRate || 0;
              currentRate = currentRate > 1 ? currentRate / 100 : currentRate;

              // Component 1: Beginning balance items
              const beginningBalanceForTaxRate = adjustmentRow['BeginningBalance'] || 0;
              const deferredTrueUpForTaxRate = adjustmentRow['DeferredTrueUp'] || 0;
              const purchaseAccountingForTaxRate = adjustmentRow['PurchaseAccounting'] || 0;
              const taxRateComponent1 = (beginningBalanceForTaxRate + deferredTrueUpForTaxRate + purchaseAccountingForTaxRate) * (endingDeferredRate - beginningDeferredRate);

              // Component 2: Return to provision
              const returnToProvisionForTaxRate = adjustmentRow['ReturnToProvision'] || 0;
              const taxRateComponent2 = returnToProvisionForTaxRate * (endingDeferredRate - priorCurrentRate);

              // Component 3: Current year activity
              const currentYearActivityForTaxRate = adjustmentRow['CurrentActivityPL'] || 0;
              const taxRateComponent3 = currentYearActivityForTaxRate * (endingDeferredRate - currentRate);

              // Component 4: Other adjustments
              const deferredExpAdjForTaxRate = adjustmentRow['DeferredExpAdjustment'] || 0;
              const balanceSheetAdjForTaxRate = adjustmentRow['BalanceSheetAdjustment'] || 0;
              const ociForTaxRate = adjustmentRow['OCIAdjustment'] || 0;
              const customDeferredAdjForTaxRate = adjustmentRow['CustomDeferredExpAdjustment'] || 0;
              const customBalanceSheetAdjForTaxRate = adjustmentRow['CustomBalanceSheetAdjustment'] || 0;
              const taxRateComponent4 = (deferredExpAdjForTaxRate + balanceSheetAdjForTaxRate + ociForTaxRate + customDeferredAdjForTaxRate + customBalanceSheetAdjForTaxRate) * (endingDeferredRate - currentRate);

              // Calculate total Tax Rate Change
              const taxRateChange = taxRateComponent1 + taxRateComponent2 + taxRateComponent3 + taxRateComponent4;

              adjustmentRow['TaxRateChange_TaxEffected'] = taxRateChange;
              adjustmentRow['TaxRateChange'] = -taxRateChange;

              // Note: ReturnToProvision and DeferredTrueUp pretax columns should keep their original pretax values
              // They are already populated correctly in the activity type loop above
              // Do NOT overwrite them with tax-effected values

              // Current Activity - actual tax effected value
              adjustmentRow['CurrentActivity'] = -(adjustmentRow['CurrentActivityPL_TaxEffected'] || 0);

              // Write-Off = (Deferred Exp Adjustment + Custom Deferred Exp Adjustment) tax effected
              const deferredExpAdjustmentTaxEffected = adjustmentRow['DeferredExpAdjustment_TaxEffected'] || 0;
              const customDeferredExpAdjustmentTaxEffected = adjustmentRow['CustomDeferredExpAdjustment_TaxEffected'] || 0;
              adjustmentRow['WriteOff'] = -(deferredExpAdjustmentTaxEffected + customDeferredExpAdjustmentTaxEffected);

              // Create dedicated rollforward columns with actual tax-effected values
              adjustmentRow['ReturnToProvisionRollforward'] = -(adjustmentRow['ReturnToProvision_TaxEffected'] || 0);
              adjustmentRow['DeferredTrueUpRollforward'] = -(adjustmentRow['DeferredTrueUp_TaxEffected'] || 0);

              // Use the displayed column values (actual tax-effected, no pre-negation) for formula components
              const taxRateChangeDisplay = adjustmentRow['TaxRateChange'] || 0;
              const returnToProvisionDisplay = adjustmentRow['ReturnToProvisionRollforward'] || 0;
              const deferredTrueUpDisplay = adjustmentRow['DeferredTrueUpRollforward'] || 0;
              const currentActivityDisplay = adjustmentRow['CurrentActivity'] || 0;
              const writeOffDisplay = adjustmentRow['WriteOff'] || 0;

              // Valuation Allowance - only populate for VALUATION ALLOWANCE and TAX EFFECTED VALUATION ALLOWANCE sections
              // Formula: Tax Rate Change + Current Activity + Deferred True-Up + Return to Provision (components are already negated)
              if (header === 'VALUATION ALLOWANCE' || header === 'TAX EFFECTED VALUATION ALLOWANCE') {
                adjustmentRow['ValuationAllowance'] = taxRateChangeDisplay +
                currentActivityDisplay +
                deferredTrueUpDisplay +
                returnToProvisionDisplay;
              } else {
                adjustmentRow['ValuationAllowance'] = 0;
              }

              const valuationAllowance = adjustmentRow['ValuationAllowance'] || 0;

              if (header === 'VALUATION ALLOWANCE' || header === 'TAX EFFECTED VALUATION ALLOWANCE') {
                // For VALUATION ALLOWANCE sections, Deferred Expense equals ValuationAllowance
                adjustmentRow['DeferredExpense'] = valuationAllowance;

                // Hide the 5 specific rollforward columns
                adjustmentRow['WriteOff'] = null;
                adjustmentRow['TaxRateChange'] = null;
                adjustmentRow['ReturnToProvisionRollforward'] = null;
                adjustmentRow['DeferredTrueUpRollforward'] = null;
                adjustmentRow['CurrentActivity'] = null;
              } else {
                // DeferredExpense = Tax Rate Change + Return to Provision + Deferred True-Up + Current Activity + Write-Off + Valuation Allowance
                adjustmentRow['DeferredExpense'] = taxRateChangeDisplay + returnToProvisionDisplay + deferredTrueUpDisplay + currentActivityDisplay + writeOffDisplay + valuationAllowance;
              }

              deferredRows.push(adjustmentRow);
            }
          });

          // Add section total row for this entity and category (only if there was data)
          const sectionTotalRow = {
            id: `total-${entityCode}-${headerIndex}`,
            EntityCode: '',
            AdjustmentCode: '',
            AdjustmentName: `TOTAL ${header}`,
            isSubFooter: true
          };

          // Calculate totals for each activity type from the section's adjustment rows
          ACTIVITY_TYPES.forEach((activityType) => {
            let total = 0;
            let taxEffectedTotal = 0;
            // Find all adjustment rows added for this section (since the last section header)
            const sectionStartIndex = deferredRows.findLastIndex((row) => row.isSection);
            const sectionRows = deferredRows.slice(sectionStartIndex + 1);

            sectionRows.forEach((row) => {
              if (!row.isSection && !row.isSubFooter) {
                total += parseFloat(row[activityType]) || 0;
                taxEffectedTotal += parseFloat(row[`${activityType}_TaxEffected`]) || 0;
              }
            });

            // For TAX EFFECTED TEMPORARY DIFFERENCES, VALUATION ALLOWANCE, and TAX EFFECTED VALUATION ALLOWANCE sections, keep (pretax) columns blank in totals
            if (adjustmentType === 'Tax Effected Temporary' || adjustmentType === 'Valuation Allowance' || adjustmentType === 'Tax Effected Valuation Allowance') {
              sectionTotalRow[activityType] = null;
            } else {
              sectionTotalRow[activityType] = total;
            }
            sectionTotalRow[`${activityType}_TaxEffected`] = taxEffectedTotal;
          });

          // Calculate totals for CTA Adjustment and Ending Balance columns
          let ctaAdjustmentTotal = 0;
          let ctaAdjustmentTaxEffectedTotal = 0;
          let endingBalanceTotal = 0;
          let endingBalanceTaxEffectedTotal = 0;

          const sectionStartIndex = deferredRows.findLastIndex((row) => row.isSection);
          const sectionRows = deferredRows.slice(sectionStartIndex + 1);

          // Calculate totals for new columns
          let deferredExpenseTotal = 0;
          let taxRateChangeTotal = 0;
          let taxRateChangeTaxEffectedTotal = 0;
          let returnToProvisionTotal = 0;
          let deferredTrueUpTotal = 0;
          let returnToProvisionRollforwardTotal = 0;
          let deferredTrueUpRollforwardTotal = 0;
          let currentActivityTotal = 0;
          let writeOffTotal = 0;
          let valuationAllowanceTotal = 0;

          sectionRows.forEach((row) => {
            if (!row.isSection && !row.isSubFooter) {
              ctaAdjustmentTotal += parseFloat(row['CTAAdjustment']) || 0;
              ctaAdjustmentTaxEffectedTotal += parseFloat(row['CTAAdjustment_TaxEffected']) || 0;
              endingBalanceTotal += parseFloat(row['EndingBalance']) || 0;
              endingBalanceTaxEffectedTotal += parseFloat(row['EndingBalance_TaxEffected']) || 0;

              // Add totals for new columns
              deferredExpenseTotal += parseFloat(row['DeferredExpense']) || 0;
              taxRateChangeTotal += parseFloat(row['TaxRateChange']) || 0;
              taxRateChangeTaxEffectedTotal += parseFloat(row['TaxRateChange_TaxEffected']) || 0;
              returnToProvisionTotal += parseFloat(row['ReturnToProvision']) || 0;
              deferredTrueUpTotal += parseFloat(row['DeferredTrueUp']) || 0;
              returnToProvisionRollforwardTotal += parseFloat(row['ReturnToProvisionRollforward']) || 0;
              deferredTrueUpRollforwardTotal += parseFloat(row['DeferredTrueUpRollforward']) || 0;
              currentActivityTotal += parseFloat(row['CurrentActivity']) || 0;
              writeOffTotal += parseFloat(row['WriteOff']) || 0;
              valuationAllowanceTotal += parseFloat(row['ValuationAllowance']) || 0;
            }
          });

          sectionTotalRow['CTAAdjustment'] = ctaAdjustmentTotal;
          sectionTotalRow['CTAAdjustment_TaxEffected'] = ctaAdjustmentTaxEffectedTotal;
          sectionTotalRow['EndingBalance'] = endingBalanceTotal;
          sectionTotalRow['EndingBalance_TaxEffected'] = endingBalanceTaxEffectedTotal;

          // Set totals for new columns
          sectionTotalRow['DeferredExpense'] = deferredExpenseTotal;
          sectionTotalRow['TaxRateChange'] = taxRateChangeTotal;
          sectionTotalRow['TaxRateChange_TaxEffected'] = taxRateChangeTaxEffectedTotal;
          sectionTotalRow['ReturnToProvision'] = returnToProvisionTotal;
          sectionTotalRow['DeferredTrueUp'] = deferredTrueUpTotal;
          sectionTotalRow['ReturnToProvisionRollforward'] = returnToProvisionRollforwardTotal;
          sectionTotalRow['DeferredTrueUpRollforward'] = deferredTrueUpRollforwardTotal;
          sectionTotalRow['CurrentActivity'] = currentActivityTotal;
          sectionTotalRow['WriteOff'] = writeOffTotal;
          sectionTotalRow['ValuationAllowance'] = valuationAllowanceTotal;

          // For VALUATION ALLOWANCE section totals, hide the 5 specific rollforward columns
          if (header === 'VALUATION ALLOWANCE' || header === 'TAX EFFECTED VALUATION ALLOWANCE') {
            sectionTotalRow['TaxRateChange'] = null;
            sectionTotalRow['ReturnToProvisionRollforward'] = null;
            sectionTotalRow['DeferredTrueUpRollforward'] = null;
            sectionTotalRow['CurrentActivity'] = null;
            sectionTotalRow['WriteOff'] = null;
          }

          deferredRows.push(sectionTotalRow);
        }
      });

      // Add grand total row after all categories for this entity
      const grandTotalRow = {
        id: `grand-total-${entityCode}`,
        EntityCode: '',
        AdjustmentCode: '',
        AdjustmentName: 'GRAND TOTAL TEMPORARY DIFFERENCES',
        isSubFooter: true
      };

      // Calculate grand totals for each activity type from all sections for this entity
      ACTIVITY_TYPES.forEach((activityType) => {
        let grandTotal = 0;
        let grandTaxEffectedTotal = 0;
        let currentSectionType = null;

        // Find all adjustment rows for this entity (not section headers or totals)
        deferredRows.forEach((row) => {
          // Track current section type
          if (row.isSection && row.AdjustmentName && row.AdjustmentName.includes(entityCode)) {
            // Extract section type from section header
            if (row.AdjustmentName.includes('TAX EFFECTED TEMPORARY DIFFERENCES')) {
              currentSectionType = 'Tax Effected Temporary';
            } else if (row.AdjustmentName.includes('VALUATION ALLOWANCE') && row.AdjustmentName.includes('TAX EFFECTED')) {
              currentSectionType = 'Tax Effected Valuation Allowance';
            } else if (row.AdjustmentName.includes('VALUATION ALLOWANCE')) {
              currentSectionType = 'Valuation Allowance';
            } else {
              currentSectionType = 'Other';
            }
          }

          if (!row.isSection && !row.isSubFooter && row.EntityCode === entityCode) {
            // For TAX EFFECTED sections and VALUATION ALLOWANCE sections, don't include (pretax) columns in grand total
            if (currentSectionType !== 'Tax Effected Temporary' && currentSectionType !== 'Valuation Allowance' && currentSectionType !== 'Tax Effected Valuation Allowance') {
              grandTotal += parseFloat(row[activityType]) || 0;
            }
            grandTaxEffectedTotal += parseFloat(row[`${activityType}_TaxEffected`]) || 0;
          }
        });

        grandTotalRow[activityType] = grandTotal;
        grandTotalRow[`${activityType}_TaxEffected`] = grandTaxEffectedTotal;
      });

      // Calculate grand totals for CTA Adjustment, Ending Balance, and new columns
      let grandCtaAdjustmentTotal = 0;
      let grandCtaAdjustmentTaxEffectedTotal = 0;
      let grandEndingBalanceTotal = 0;
      let grandEndingBalanceTaxEffectedTotal = 0;
      let grandDeferredExpenseTotal = 0;
      let grandTaxRateChangeTotal = 0;
      let grandTaxRateChangeTaxEffectedTotal = 0;
      let grandReturnToProvisionTotal = 0;
      let grandDeferredTrueUpTotal = 0;
      let grandReturnToProvisionRollforwardTotal = 0;
      let grandDeferredTrueUpRollforwardTotal = 0;
      let grandCurrentActivityTotal = 0;
      let grandWriteOffTotal = 0;
      let grandValuationAllowanceTotal = 0;

      deferredRows.forEach((row) => {
        if (!row.isSection && !row.isSubFooter && row.EntityCode === entityCode) {
          grandCtaAdjustmentTotal += parseFloat(row['CTAAdjustment']) || 0;
          grandCtaAdjustmentTaxEffectedTotal += parseFloat(row['CTAAdjustment_TaxEffected']) || 0;
          grandEndingBalanceTotal += parseFloat(row['EndingBalance']) || 0;
          grandEndingBalanceTaxEffectedTotal += parseFloat(row['EndingBalance_TaxEffected']) || 0;

          // Add grand totals for new columns
          grandDeferredExpenseTotal += parseFloat(row['DeferredExpense']) || 0;
          grandTaxRateChangeTotal += parseFloat(row['TaxRateChange']) || 0;
          grandTaxRateChangeTaxEffectedTotal += parseFloat(row['TaxRateChange_TaxEffected']) || 0;
          grandReturnToProvisionTotal += parseFloat(row['ReturnToProvision']) || 0;
          grandDeferredTrueUpTotal += parseFloat(row['DeferredTrueUp']) || 0;
          grandReturnToProvisionRollforwardTotal += parseFloat(row['ReturnToProvisionRollforward']) || 0;
          grandDeferredTrueUpRollforwardTotal += parseFloat(row['DeferredTrueUpRollforward']) || 0;
          grandCurrentActivityTotal += parseFloat(row['CurrentActivity']) || 0;
          grandWriteOffTotal += parseFloat(row['WriteOff']) || 0;
          grandValuationAllowanceTotal += parseFloat(row['ValuationAllowance']) || 0;
        }
      });

      grandTotalRow['CTAAdjustment'] = grandCtaAdjustmentTotal;
      grandTotalRow['CTAAdjustment_TaxEffected'] = grandCtaAdjustmentTaxEffectedTotal;
      grandTotalRow['EndingBalance'] = grandEndingBalanceTotal;
      grandTotalRow['EndingBalance_TaxEffected'] = grandEndingBalanceTaxEffectedTotal;

      // Set grand totals for new columns
      grandTotalRow['DeferredExpense'] = grandDeferredExpenseTotal;
      grandTotalRow['TaxRateChange'] = grandTaxRateChangeTotal;
      grandTotalRow['TaxRateChange_TaxEffected'] = grandTaxRateChangeTaxEffectedTotal;
      grandTotalRow['ReturnToProvision'] = grandReturnToProvisionTotal;
      grandTotalRow['DeferredTrueUp'] = grandDeferredTrueUpTotal;
      grandTotalRow['ReturnToProvisionRollforward'] = grandReturnToProvisionRollforwardTotal;
      grandTotalRow['DeferredTrueUpRollforward'] = grandDeferredTrueUpRollforwardTotal;
      grandTotalRow['CurrentActivity'] = grandCurrentActivityTotal;
      grandTotalRow['WriteOff'] = grandWriteOffTotal;
      grandTotalRow['ValuationAllowance'] = grandValuationAllowanceTotal;

      deferredRows.push(grandTotalRow);
    });

    return { deferredRows: annotateDeferredDrilldowns(deferredRows), activityTypes: ACTIVITY_TYPES };
  };

  // Pivot data to use entities as columns and adjustments as rows, grouped by sections
  const pivotData = (data, deferredSourceData = data) => {
    // Always use scenario entityList for consistent alphabetical column ordering
    // This prevents columns from reordering when data changes
    let uniqueEntities;
    if (selectedScenario?.entityList && selectedScenario.entityList.length > 0) {
      // Use scenario's entity list, sorted alphabetically
      uniqueEntities = [...selectedScenario.entityList].sort();
    } else {
      // Fallback: use entities that have data, sorted alphabetically
      uniqueEntities = [...new Set(data.map((row) => row.EntityCode))].
      filter((code) => code).
      sort();
    }

    // Precompute entityInput map for O(1) lookups inside pivotData (replaces repeated .find())
    const entityInputMap = new Map(
      entityInputs.
      filter((ei) => ei.ScenarioCode === selectedScenario?.scenarioCode).
      map((ei) => [ei.EntityCode, ei])
    );

    // Group adjustments by their adjustment type (which maps to sections)
    const groupedByType = {};
    data.forEach((row) => {
      const adjustmentType = row.AdjustmentSource || row.AdjustmentType || 'Other';
      if (!groupedByType[adjustmentType]) {
        groupedByType[adjustmentType] = [];
      }
      groupedByType[adjustmentType].push(row);
    });

    const pivotedRows = [];
    let isFirstSection = true;
    let sectionTotalsCache = {}; // Cache to store section totals for calculations

    // For ETR view, add TAX EXPENSE section at the top
    // We'll populate the values after calculating them, then insert at the beginning
    let taxExpenseSection = null;
    if (calculationType === 'effective-tax-rate') {
      taxExpenseSection = {
        header: {
          id: 'section-tax-expense',
          AdjustmentCode: '',
          AdjustmentName: 'TAX EXPENSE',
          isSection: true
        },
        currentTaxExpense: {
          id: 'etr-current-tax-expense',
          AdjustmentCode: '',
          AdjustmentName: 'Current Tax Expense',
          isSection: false
        },
        deferredTaxExpense: {
          id: 'etr-deferred-tax-expense',
          AdjustmentCode: '',
          AdjustmentName: 'Deferred Tax Expense',
          isSection: false
        },
        totalTaxExpense: {
          id: 'etr-total-tax-expense-top',
          AdjustmentCode: '',
          AdjustmentName: 'Total Tax Expense',
          isSubFooter: true
        }
      };

      // Initialize entity columns
      uniqueEntities.forEach((entityCode) => {
        taxExpenseSection.header[entityCode] = null;
        taxExpenseSection.currentTaxExpense[entityCode] = 0;
        taxExpenseSection.deferredTaxExpense[entityCode] = 0;
        taxExpenseSection.totalTaxExpense[entityCode] = 0;
      });
    }

    // Process each section in order - show all sections regardless of data
    Object.entries(sectionMapping).forEach(([adjustmentType, sectionTitle]) => {
      const sectionData = groupedByType[adjustmentType];

      // Add sub-footer for previous section (except for the first section)
      if (!isFirstSection && pivotedRows.length > 0) {
        // Calculate totals for the previous section
        const previousAdjustmentType = Object.keys(sectionMapping)[Object.keys(sectionMapping).indexOf(adjustmentType) - 1];
        const sectionTotals = {
          id: `footer-${adjustmentType}`,
          AdjustmentCode: '',
          AdjustmentName: sectionTotalTitles[previousAdjustmentType] || 'Section Total',
          isSubFooter: true
        };

        // Calculate totals for each entity column
        const currentSectionTotals = {};
        uniqueEntities.forEach((ec) => {currentSectionTotals[ec] = 0;});
        // Single backward scan for all entities at once (replaces N separate scans)
        for (let i = pivotedRows.length - 1; i >= 0; i--) {
          const row = pivotedRows[i];
          if (row.isSection) break;
          if (!row.isSubFooter) {
            uniqueEntities.forEach((ec) => {currentSectionTotals[ec] += parseFloat(row[ec]) || 0;});
          }
        }
        uniqueEntities.forEach((ec) => {sectionTotals[ec] = currentSectionTotals[ec];});

        // Cache the section totals for later calculations
        sectionTotalsCache[previousAdjustmentType] = currentSectionTotals;

        pivotedRows.push(sectionTotals);

        // Add special section after Pretax section (PRETAX AT GROUP RATE) - ETR ONLY
        if (previousAdjustmentType === 'Pretax' && calculationType === 'effective-tax-rate') {
          // Add section header
          const pretaxAtGroupRateHeader = {
            id: 'section-pretax-at-group-rate',
            AdjustmentCode: '',
            AdjustmentName: 'PRETAX AT GROUP RATE',
            isSection: true
          };
          uniqueEntities.forEach((entityCode) => {
            pretaxAtGroupRateHeader[entityCode] = null;
          });
          pivotedRows.push(pretaxAtGroupRateHeader);

          // Add Group Tax Rate row
          const groupTaxRateRow = {
            id: 'group-tax-rate',
            AdjustmentCode: '',
            AdjustmentName: 'Group Tax Rate',
            isSection: false
          };

          // Get domestic tax rate from selected scenario
          const domesticTaxRate = parseFloat(selectedScenario?.DomesticTaxRate) || 0;
          const domesticTaxRateDecimal = domesticTaxRate > 1 ? domesticTaxRate / 100 : domesticTaxRate;
          const domesticTaxRatePercentage = domesticTaxRate > 1 ? domesticTaxRate : domesticTaxRate * 100;

          // Populate each entity column with the group rate as a percentage string
          uniqueEntities.forEach((entityCode) => {
            groupTaxRateRow[entityCode] = `${domesticTaxRatePercentage.toFixed(2)}%`;
          });
          pivotedRows.push(groupTaxRateRow);

          // Add GAAP PRETAX BOOK INCOME AT GROUP RATE total row
          const gaapPretaxAtGroupRate = {
            id: 'calculated-gaap-pretax-at-group-rate',
            AdjustmentCode: '',
            AdjustmentName: 'GAAP PRETAX BOOK INCOME AT GROUP RATE',
            isSubFooter: true
          };

          // Calculate: GAAP PRETAX INCOME (Pretax total) × Group Rate
          uniqueEntities.forEach((entityCode) => {
            const pretaxTotal = sectionTotalsCache['Pretax']?.[entityCode] || 0;
            gaapPretaxAtGroupRate[entityCode] = pretaxTotal * domesticTaxRateDecimal;
          });

          // Cache this value for potential later use
          sectionTotalsCache['GAAP Pretax at Group Rate'] = {};
          uniqueEntities.forEach((entityCode) => {
            sectionTotalsCache['GAAP Pretax at Group Rate'][entityCode] = gaapPretaxAtGroupRate[entityCode];
          });

          pivotedRows.push(gaapPretaxAtGroupRate);

          // Add FOREIGN RATE DIFFERENTIAL section
          const foreignRateDiffHeader = {
            id: 'section-foreign-rate-differential',
            AdjustmentCode: '',
            AdjustmentName: 'FOREIGN RATE DIFFERENTIAL',
            isSection: true
          };
          uniqueEntities.forEach((entityCode) => {
            foreignRateDiffHeader[entityCode] = null;
          });
          pivotedRows.push(foreignRateDiffHeader);

          // Row 1: Current Tax Rate
          const currentTaxRateRow = {
            id: 'foreign-rate-diff-current-tax-rate',
            AdjustmentCode: '',
            AdjustmentName: 'Current Tax Rate',
            isSubFooter: true
          };

          // Get current tax rate from EntityInput for each entity (same as in current provision)
          const currentTaxRates = {};
          uniqueEntities.forEach((entityCode) => {
            const entityInput = entityInputMap.get(entityCode);
            const taxRate = parseFloat(entityInput?.CurrentTaxRate) || 0;
            const taxRatePercentage = taxRate > 1 ? taxRate : taxRate * 100;
            const taxRateDecimal = taxRate > 1 ? taxRate / 100 : taxRate;

            currentTaxRateRow[entityCode] = `${taxRatePercentage.toFixed(2)}%`;
            currentTaxRates[entityCode] = taxRateDecimal; // Store decimal for calculations
          });
          pivotedRows.push(currentTaxRateRow);

          // Row 2: Pretax at current tax rate
          const pretaxAtCurrentRate = {
            id: 'pretax-at-current-tax-rate',
            AdjustmentCode: '',
            AdjustmentName: 'Pretax at current tax rate',
            isSubFooter: true
          };

          // Calculate: GAAP PRETAX INCOME × Current Tax Rate
          uniqueEntities.forEach((entityCode) => {
            const pretaxTotal = sectionTotalsCache['Pretax']?.[entityCode] || 0;
            const currentRate = currentTaxRates[entityCode] || 0;
            pretaxAtCurrentRate[entityCode] = pretaxTotal * currentRate;
          });

          // Cache for next calculation
          const pretaxAtCurrentRateValues = {};
          uniqueEntities.forEach((entityCode) => {
            pretaxAtCurrentRateValues[entityCode] = pretaxAtCurrentRate[entityCode];
          });

          pivotedRows.push(pretaxAtCurrentRate);

          // Row 3: TOTAL FOREIGN RATE DIFFERENTIAL
          const totalForeignRateDiff = {
            id: 'total-foreign-rate-differential',
            AdjustmentCode: '',
            AdjustmentName: 'TOTAL FOREIGN RATE DIFFERENTIAL',
            isSubFooter: true
          };

          // Calculate: Pretax at current tax rate - GAAP PRETAX BOOK INCOME AT GROUP RATE
          uniqueEntities.forEach((entityCode) => {
            const pretaxAtCurrent = pretaxAtCurrentRateValues[entityCode] || 0;
            const gaapPretaxAtGroup = sectionTotalsCache['GAAP Pretax at Group Rate']?.[entityCode] || 0;
            totalForeignRateDiff[entityCode] = pretaxAtCurrent - gaapPretaxAtGroup;
          });

          // Cache for potential later use
          sectionTotalsCache['Total Foreign Rate Differential'] = {};
          uniqueEntities.forEach((entityCode) => {
            sectionTotalsCache['Total Foreign Rate Differential'][entityCode] = totalForeignRateDiff[entityCode];
          });

          pivotedRows.push(totalForeignRateDiff);
        }

        // Add special calculated total after GS Temporary section
        if (previousAdjustmentType === 'GS Temporary') {
          const calculatedTotal = {
            id: 'calculated-gaap-book-income',
            AdjustmentCode: '',
            AdjustmentName: 'STATUTORY PRETAX BOOK INCOME',
            isCalculatedTotal: true
          };

          // Calculate: Pretax total + GS Permanent total + GS Temporary total
          uniqueEntities.forEach((entityCode) => {
            const pretaxTotal = sectionTotalsCache['Pretax']?.[entityCode] || 0;
            const gsPermTotal = sectionTotalsCache['GS Permanent']?.[entityCode] || 0;
            const gsTempTotal = currentSectionTotals[entityCode] || 0;

            calculatedTotal[entityCode] = pretaxTotal + gsPermTotal + gsTempTotal;
          });

          // Cache the statutory pretax book income for later calculations
          sectionTotalsCache['Statutory Pretax'] = {};
          uniqueEntities.forEach((entityCode) => {
            sectionTotalsCache['Statutory Pretax'][entityCode] = calculatedTotal[entityCode];
          });

          pivotedRows.push(calculatedTotal);
        }

        // Add special calculated total after Temporary section (STAT TO TAX TEMPORARY DIFFERENCES)
        if (previousAdjustmentType === 'Temporary') {
          const taxableIncomeTotal = {
            id: 'calculated-taxable-income-before-adjustments',
            AdjustmentCode: '',
            AdjustmentName: 'TAXABLE INCOME BEFORE ADJUSTMENTS',
            isCalculatedTotal: true
          };

          // Calculate: Statutory Pretax Book Income + Permanent + Payable + Temporary totals
          uniqueEntities.forEach((entityCode) => {
            const statutoryPretaxTotal = sectionTotalsCache['Statutory Pretax']?.[entityCode] || 0;
            const permanentTotal = sectionTotalsCache['Permanent']?.[entityCode] || 0;
            const payableTotal = sectionTotalsCache['Payable']?.[entityCode] || 0;
            const temporaryTotal = currentSectionTotals[entityCode] || 0;

            taxableIncomeTotal[entityCode] = statutoryPretaxTotal + permanentTotal + payableTotal + temporaryTotal;
          });

          // Cache the taxable income before adjustments for later calculations
          sectionTotalsCache['Taxable Income Before Adjustments'] = {};
          uniqueEntities.forEach((entityCode) => {
            sectionTotalsCache['Taxable Income Before Adjustments'][entityCode] = taxableIncomeTotal[entityCode];
          });

          pivotedRows.push(taxableIncomeTotal);
        }

        // Add special calculated total after TI temporary section (TAXABLE INCOME TEMP ADJUSTMENT)
        if (previousAdjustmentType === 'TI temporary') {
          const taxableIncomeBeforeNOL = {
            id: 'calculated-taxable-income-before-nol',
            AdjustmentCode: '',
            AdjustmentName: 'TAXABLE INCOME BEFORE NET OPERATING LOSS',
            isCalculatedTotal: true
          };

          // Calculate: Taxable Income Before Adjustments + TI Permanent + TI Temporary totals
          uniqueEntities.forEach((entityCode) => {
            const taxableIncomeBeforeAdjTotal = sectionTotalsCache['Taxable Income Before Adjustments']?.[entityCode] || 0;
            const tiPermTotal = sectionTotalsCache['TI Permanent']?.[entityCode] || 0;
            const tiTempTotal = currentSectionTotals[entityCode] || 0;

            // Debug logging






            taxableIncomeBeforeNOL[entityCode] = taxableIncomeBeforeAdjTotal + tiPermTotal + tiTempTotal;
          });

          // Cache the taxable income before NOL for later calculations
          sectionTotalsCache['Taxable Income Before NOL'] = {};
          uniqueEntities.forEach((entityCode) => {
            sectionTotalsCache['Taxable Income Before NOL'][entityCode] = taxableIncomeBeforeNOL[entityCode];
          });

          pivotedRows.push(taxableIncomeBeforeNOL);
        }

        // Add TAXABLE INCOME BEFORE NOL calculation right before Net Operating Loss section
        // This ensures it runs even if TI temporary section doesn't exist
        if (adjustmentType === 'Net Operating Loss') {
          // Only add if we haven't already added it after TI temporary section
          const alreadyAdded = pivotedRows.some((row) => row.id === 'calculated-taxable-income-before-nol');
          if (!alreadyAdded) {
            const taxableIncomeBeforeNOL = {
              id: 'calculated-taxable-income-before-nol',
              AdjustmentCode: '',
              AdjustmentName: 'TAXABLE INCOME BEFORE NET OPERATING LOSS',
              isCalculatedTotal: true
            };

            // Calculate: Taxable Income Before Adjustments + TI Permanent + TI Temporary totals
            uniqueEntities.forEach((entityCode) => {
              const taxableIncomeBeforeAdjTotal = sectionTotalsCache['Taxable Income Before Adjustments']?.[entityCode] || 0;
              const tiPermTotal = sectionTotalsCache['TI Permanent']?.[entityCode] || 0;
              const tiTempTotal = sectionTotalsCache['TI Temporary']?.[entityCode] || 0;

              // Debug logging






              taxableIncomeBeforeNOL[entityCode] = taxableIncomeBeforeAdjTotal + tiPermTotal + tiTempTotal;
            });

            // Cache the taxable income before NOL for later calculations
            sectionTotalsCache['Taxable Income Before NOL'] = {};
            uniqueEntities.forEach((entityCode) => {
              sectionTotalsCache['Taxable Income Before NOL'][entityCode] = taxableIncomeBeforeNOL[entityCode];
            });

            pivotedRows.push(taxableIncomeBeforeNOL);
          }
        }

        // Add special calculated total after Net Operating Loss section
        if (previousAdjustmentType === 'Net Operating Loss') {
          const totalTaxableIncome = {
            id: 'calculated-total-taxable-income',
            AdjustmentCode: '',
            AdjustmentName: 'TOTAL TAXABLE INCOME',
            isCalculatedTotal: true
          };

          // Calculate: TOTAL NET OPERATING LOSS + Taxable Income Before NOL
          uniqueEntities.forEach((entityCode) => {
            // Use cached "Taxable Income Before NOL" value from earlier calculation
            const taxableIncomeBeforeNOL = sectionTotalsCache['Taxable Income Before NOL']?.[entityCode] || 0;
            const netOperatingLossTotal = currentSectionTotals[entityCode] || 0;

            // Debug logging





            totalTaxableIncome[entityCode] = netOperatingLossTotal + taxableIncomeBeforeNOL;
          });

          // Cache the total taxable income for later calculations
          sectionTotalsCache['Total Taxable Income'] = {};
          uniqueEntities.forEach((entityCode) => {
            sectionTotalsCache['Total Taxable Income'][entityCode] = totalTaxableIncome[entityCode];
          });

          pivotedRows.push(totalTaxableIncome);

          // Add current tax rate row immediately after total taxable income
          const currentTaxRate = {
            id: 'calculated-current-tax-rate',
            AdjustmentCode: '',
            AdjustmentName: 'CURRENT TAX RATE PERCENTAGE',
            isCalculatedTotal: true
          };

          // Get current tax rate from EntityInput for each entity
          sectionTotalsCache['Current Tax Rate'] = {};
          uniqueEntities.forEach((entityCode) => {
            const entityInput = entityInputMap.get(entityCode);
            const taxRate = parseFloat(entityInput?.CurrentTaxRate) || 0;
            const taxRatePercentage = taxRate > 1 ? taxRate : taxRate * 100;
            const taxRateDecimal = taxRate > 1 ? taxRate / 100 : taxRate;
            currentTaxRate[entityCode] = `${taxRatePercentage.toFixed(2)}%`;
            sectionTotalsCache['Current Tax Rate'][entityCode] = taxRateDecimal;
          });

          pivotedRows.push(currentTaxRate);

          // Add current tax expense before adjustments row
          const currentTaxExpense = {
            id: 'calculated-current-tax-expense',
            AdjustmentCode: '',
            AdjustmentName: 'CURRENT TAX EXPENSE BEFORE ADJUSTMENTS',
            isCalculatedTotal: true
          };

          // Calculate: Total Taxable Income × Current Tax Rate
          // For ETR view, permanent sections are already tax-effected, so we need to calculate differently
          uniqueEntities.forEach((entityCode) => {
            const currentTaxRateValue = sectionTotalsCache['Current Tax Rate']?.[entityCode] || 0;

            if (calculationType === 'effective-tax-rate') {
              // In ETR, permanent sections are already tax-effected
              // Current Tax Expense = (Pretax × rate) + (GS Temp × rate) + GS Perm (already tax-effected) + Perm (already tax-effected) + Payable (already tax-effected) + (TI Temp × rate) + TI Perm (already tax-effected) + (NOL × rate)
              const pretaxTotal = sectionTotalsCache['Pretax']?.[entityCode] || 0;
              const gsTempTotal = sectionTotalsCache['GS Temporary']?.[entityCode] || 0;
              const gsPermTotal = sectionTotalsCache['GS Permanent']?.[entityCode] || 0; // Already tax-effected
              const permanentTotal = sectionTotalsCache['Permanent']?.[entityCode] || 0; // Already tax-effected
              const payableTotal = sectionTotalsCache['Payable']?.[entityCode] || 0; // Already tax-effected
              const temporaryTotal = sectionTotalsCache['Temporary']?.[entityCode] || 0;
              const tiPermTotal = sectionTotalsCache['TI Permanent']?.[entityCode] || 0; // Already tax-effected
              const tiTempTotal = sectionTotalsCache['TI Temporary']?.[entityCode] || 0;
              const nolTotal = sectionTotalsCache['Net Operating Loss']?.[entityCode] || 0;

              // Tax-effect the pretax sections and add the already tax-effected permanent sections
              currentTaxExpense[entityCode] =
              pretaxTotal * currentTaxRateValue +
              gsTempTotal * currentTaxRateValue +
              gsPermTotal + // Already tax-effected
              permanentTotal + // Already tax-effected
              payableTotal + // Already tax-effected
              temporaryTotal * currentTaxRateValue +
              tiPermTotal + // Already tax-effected
              tiTempTotal * currentTaxRateValue +
              nolTotal * currentTaxRateValue;
            } else {
              // For current provision, use the standard calculation
              const totalTaxableIncomeValue = sectionTotalsCache['Total Taxable Income']?.[entityCode] || 0;
              currentTaxExpense[entityCode] = totalTaxableIncomeValue * currentTaxRateValue;
            }
          });

          // Cache the current tax expense for later calculations
          sectionTotalsCache['Current Tax Expense Before Adjustments'] = {};
          uniqueEntities.forEach((entityCode) => {
            sectionTotalsCache['Current Tax Expense Before Adjustments'][entityCode] = currentTaxExpense[entityCode];
          });

          pivotedRows.push(currentTaxExpense);
        }

        // Add special calculated total after Tax Adj. Return section (RETURN BASIS TAX ADJUSTMENTS)
        if (previousAdjustmentType === 'Tax Adj. Return') {
          const totalReturnBasisTaxExpense = {
            id: 'calculated-total-return-basis-tax-expense',
            AdjustmentCode: '',
            AdjustmentName: 'TOTAL RETURN BASIS CURRENT TAX EXPENSE',
            isCalculatedTotal: true
          };

          // Calculate: Current Tax Expense Before Adjustments + Tax Effected Temporary + Return Basis Tax Adjustments
          uniqueEntities.forEach((entityCode) => {
            const currentTaxExpenseValue = sectionTotalsCache['Current Tax Expense Before Adjustments']?.[entityCode] || 0;
            const taxEffectedTempTotal = sectionTotalsCache['Tax Effected Temporary']?.[entityCode] || 0;
            const returnBasisTaxAdjTotal = currentSectionTotals[entityCode] || 0;

            totalReturnBasisTaxExpense[entityCode] = currentTaxExpenseValue + taxEffectedTempTotal + returnBasisTaxAdjTotal;
          });

          // Cache the total return basis current tax expense for later calculations
          sectionTotalsCache['Total Return Basis Current Tax Expense'] = {};
          uniqueEntities.forEach((entityCode) => {
            sectionTotalsCache['Total Return Basis Current Tax Expense'][entityCode] = totalReturnBasisTaxExpense[entityCode];
          });

          pivotedRows.push(totalReturnBasisTaxExpense);
        }

      }

      // Always add section header row
      const sectionHeaderRow = {
        id: `section-${adjustmentType}`,
        AdjustmentCode: '',
        AdjustmentName: sectionTitle,
        isSection: true
      };
      uniqueEntities.forEach((entityCode) => {
        sectionHeaderRow[entityCode] = null;
      });
      pivotedRows.push(sectionHeaderRow);

      // Add adjustment rows for this section if data exists
      if (sectionData && sectionData.length > 0) {
        // Group adjustments by adjustment code to track multiple entries
        const adjustmentCodeGroups = {};

        sectionData.forEach((row) => {
          const code = row.AdjustmentCode;
          if (!adjustmentCodeGroups[code]) {
            adjustmentCodeGroups[code] = [];
          }
          adjustmentCodeGroups[code].push(row);
        });

        // Process each adjustment code group - combine all rows with same code
        Object.keys(adjustmentCodeGroups).sort().forEach((adjustmentCode) => {
          const adjustmentsWithThisCode = adjustmentCodeGroups[adjustmentCode];
          const adjustmentName = adjustmentsWithThisCode[0]?.AdjustmentName || '';

          // Create a single combined row for this adjustment code
          const pivotedRow = {
            id: `${adjustmentType}_${adjustmentCode}_combined`,
            AdjustmentCode: adjustmentCode,
            AdjustmentName: adjustmentName,
            isSection: false
          };

          // Add entity data as columns - sum all amounts for each entity
          uniqueEntities.forEach((entityCode) => {
            // Find all adjustments for this entity and adjustment code
            const entityAdjustments = adjustmentsWithThisCode.filter((row) =>
            row.EntityCode === entityCode
            );

            // Sum all amounts for this entity
            let totalAmount = 0;
            entityAdjustments.forEach((adj) => {
              const amount = selectedCurrency === 'USD' ?
              adj.USDInput || 0 :
              adj.LocalCurrencyInput || 0;
              totalAmount += amount;
            });

            // For ETR view, apply tax-effecting to specific sections
            if (calculationType === 'effective-tax-rate') {
              const sectionsToTaxEffect = [
              'GAAP TO STAT PERMANENT DIFFERENCES',
              'STAT TO TAX PERMANENT DIFFERENCES',
              'TAXABLE INCOME PERM ADJUSTMENT',
              'DEDUCTION FOR STATE AND LOCAL TAXES'];


              if (sectionsToTaxEffect.includes(sectionTitle)) {
                // Get current tax rate for this entity
                const taxRate = getTaxRateForEntity(entityCode, selectedScenario?.scenarioCode);
                totalAmount = totalAmount * taxRate;
              }
            }

            pivotedRow[entityCode] = totalAmount;
          });

          pivotedRows.push(pivotedRow);
        });
      }

      isFirstSection = false;
    });

    // Add final sub-footer for the last section
    if (pivotedRows.length > 0) {
      const lastAdjustmentType = Object.keys(sectionMapping)[Object.keys(sectionMapping).length - 1];
      const finalSectionTotals = {
        id: `footer-final`,
        AdjustmentCode: '',
        AdjustmentName: sectionTotalTitles[lastAdjustmentType] || 'Section Total',
        isSubFooter: true
      };

      // Calculate totals for each entity column for the last section
      const currentSectionTotals = {};
      uniqueEntities.forEach((entityCode) => {
        let total = 0;
        // Find all non-header, non-footer rows since the last section header
        for (let i = pivotedRows.length - 1; i >= 0; i--) {
          const row = pivotedRows[i];
          if (row.isSection) break; // Stop at the last section header
          if (!row.isSection && !row.isSubFooter) {
            total += parseFloat(row[entityCode]) || 0;
          }
        }
        finalSectionTotals[entityCode] = total;
        currentSectionTotals[entityCode] = total;
      });

      // Cache the final section totals
      sectionTotalsCache[lastAdjustmentType] = currentSectionTotals;

      pivotedRows.push(finalSectionTotals);

      // Add the final calculated total after the last section (Tax Adj. RTP)
      if (lastAdjustmentType === 'Tax Adj. RTP') {
        const totalCurrentTaxExpense = {
          id: 'calculated-total-current-tax-expense',
          AdjustmentCode: '',
          AdjustmentName: 'TOTAL CURRENT TAX EXPENSE',
          isCalculatedTotal: true
        };

        // Calculate: Total Return Basis Current Tax Expense + Provision Basis Tax Adjustments + Return-to-Provision Adjustments
        uniqueEntities.forEach((entityCode) => {
          const returnBasisTaxExpenseValue = sectionTotalsCache['Total Return Basis Current Tax Expense']?.[entityCode] || 0;
          const provisionBasisTaxAdjTotal = sectionTotalsCache['Tax Adj. Provision']?.[entityCode] || 0;
          const returnToProvisionAdjTotal = currentSectionTotals[entityCode] || 0;

          totalCurrentTaxExpense[entityCode] = returnBasisTaxExpenseValue + provisionBasisTaxAdjTotal + returnToProvisionAdjTotal;
        });

        // For ETR view, populate the TAX EXPENSE section instead of adding these as separate rows
        if (calculationType === 'effective-tax-rate' && taxExpenseSection) {
          uniqueEntities.forEach((entityCode) => {
            taxExpenseSection.currentTaxExpense[entityCode] = totalCurrentTaxExpense[entityCode];
          });

          // Add DEFERRED TAX EXPENSES section (ETR only)
          // Get deferred rollforward data to extract grand total values
          const { deferredRows: deferredRollforwardRows } = createDeferredRollforwardData(deferredSourceData);

          const deferredTaxExpensesHeader = {
            id: 'section-deferred-tax-expenses',
            AdjustmentCode: '',
            AdjustmentName: 'DEFERRED TAX EXPENSES',
            isSection: true
          };
          uniqueEntities.forEach((entityCode) => {
            deferredTaxExpensesHeader[entityCode] = null;
          });
          pivotedRows.push(deferredTaxExpensesHeader);

          // Row 1: Deferred Tax Rate Change
          const deferredTaxRateChange = {
            id: 'deferred-tax-rate-change',
            AdjustmentCode: '',
            AdjustmentName: 'Deferred Tax Rate Change',
            isSubFooter: true
          };
          uniqueEntities.forEach((entityCode) => {
            // Find the grand total row for this entity
            const grandTotalRow = deferredRollforwardRows.find((row) =>
            row.AdjustmentName === 'GRAND TOTAL TEMPORARY DIFFERENCES' &&
            row.EntityCode === '' &&
            row.id === `grand-total-${entityCode}`
            );
            deferredTaxRateChange[entityCode] = grandTotalRow?.TaxRateChange || 0;
          });
          pivotedRows.push(deferredTaxRateChange);

          // Row 2: Deferred Return-to-Provision
          const deferredReturnToProvision = {
            id: 'deferred-return-to-provision',
            AdjustmentCode: '',
            AdjustmentName: 'Deferred Return-to-Provision',
            isSubFooter: true
          };
          uniqueEntities.forEach((entityCode) => {
            // Find the grand total row for this entity
            const grandTotalRow = deferredRollforwardRows.find((row) =>
            row.AdjustmentName === 'GRAND TOTAL TEMPORARY DIFFERENCES' &&
            row.EntityCode === '' &&
            row.id === `grand-total-${entityCode}`
            );
            deferredReturnToProvision[entityCode] = grandTotalRow?.ReturnToProvisionRollforward || 0;
          });
          pivotedRows.push(deferredReturnToProvision);

          // Row 3: Deferred True-Up
          const deferredTrueUp = {
            id: 'deferred-true-up',
            AdjustmentCode: '',
            AdjustmentName: 'Deferred True-Up',
            isSubFooter: true
          };
          uniqueEntities.forEach((entityCode) => {
            // Find the grand total row for this entity
            const grandTotalRow = deferredRollforwardRows.find((row) =>
            row.AdjustmentName === 'GRAND TOTAL TEMPORARY DIFFERENCES' &&
            row.EntityCode === '' &&
            row.id === `grand-total-${entityCode}`
            );
            deferredTrueUp[entityCode] = grandTotalRow?.DeferredTrueUpRollforward || 0;
          });
          pivotedRows.push(deferredTrueUp);

          // Row 4: Deferred Write-off
          const deferredWriteOff = {
            id: 'deferred-write-off',
            AdjustmentCode: '',
            AdjustmentName: 'Deferred Write-off',
            isSubFooter: true
          };
          uniqueEntities.forEach((entityCode) => {
            // Find the grand total row for this entity
            const grandTotalRow = deferredRollforwardRows.find((row) =>
            row.AdjustmentName === 'GRAND TOTAL TEMPORARY DIFFERENCES' &&
            row.EntityCode === '' &&
            row.id === `grand-total-${entityCode}`
            );
            deferredWriteOff[entityCode] = grandTotalRow?.WriteOff || 0;
          });
          pivotedRows.push(deferredWriteOff);

          // Row 5: Valuation Allowance
          const deferredValuationAllowance = {
            id: 'deferred-valuation-allowance',
            AdjustmentCode: '',
            AdjustmentName: 'Valuation Allowance',
            isSubFooter: true
          };
          uniqueEntities.forEach((entityCode) => {
            // Find the grand total row for this entity
            const grandTotalRow = deferredRollforwardRows.find((row) =>
            row.AdjustmentName === 'GRAND TOTAL TEMPORARY DIFFERENCES' &&
            row.EntityCode === '' &&
            row.id === `grand-total-${entityCode}`
            );
            deferredValuationAllowance[entityCode] = grandTotalRow?.ValuationAllowance || 0;
          });
          pivotedRows.push(deferredValuationAllowance);

          // Row 6: TOTAL TAX EXPENSE
          const totalTaxExpenseETR = {
            id: 'total-tax-expense-etr',
            AdjustmentCode: '',
            AdjustmentName: 'TOTAL TAX EXPENSE',
            isSubFooter: true
          };
          uniqueEntities.forEach((entityCode) => {
            // Components from earlier sections
            const gaapPretaxAtGroup = sectionTotalsCache['GAAP Pretax at Group Rate']?.[entityCode] || 0;
            const foreignRateDiff = sectionTotalsCache['Total Foreign Rate Differential']?.[entityCode] || 0;
            const gsPermTotal = sectionTotalsCache['GS Permanent']?.[entityCode] || 0;
            const permanentTotal = sectionTotalsCache['Permanent']?.[entityCode] || 0;
            const payableTotal = sectionTotalsCache['Payable']?.[entityCode] || 0;
            const tiPermTotal = sectionTotalsCache['TI Permanent']?.[entityCode] || 0;
            const taxAdjReturnTotal = sectionTotalsCache['Tax Adj. Return']?.[entityCode] || 0;
            const taxAdjProvisionTotal = sectionTotalsCache['Tax Adj. Provision']?.[entityCode] || 0;
            const taxAdjRTPTotal = sectionTotalsCache['Tax Adj. RTP']?.[entityCode] || 0;

            // Components from current section (5 deferred rows)
            const deferredRateChange = deferredTaxRateChange[entityCode] || 0;
            const deferredRTP = deferredReturnToProvision[entityCode] || 0;
            const deferredTU = deferredTrueUp[entityCode] || 0;
            const deferredWO = deferredWriteOff[entityCode] || 0;
            const deferredVA = deferredValuationAllowance[entityCode] || 0;

            totalTaxExpenseETR[entityCode] = gaapPretaxAtGroup + foreignRateDiff +
            gsPermTotal + permanentTotal + payableTotal + tiPermTotal +
            taxAdjReturnTotal + taxAdjProvisionTotal + taxAdjRTPTotal +
            deferredRateChange + deferredRTP + deferredTU + deferredWO + deferredVA;
          });
          pivotedRows.push(totalTaxExpenseETR);

        } else {
          // For current provision view, add the row as before
          pivotedRows.push(totalCurrentTaxExpense);
        }

        // Add total deferred tax expense row
        const totalDeferredTaxExpense = {
          id: 'calculated-total-deferred-tax-expense',
          AdjustmentCode: '',
          AdjustmentName: 'TOTAL DEFERRED TAX EXPENSE',
          isCalculatedTotal: true
        };

        // Calculate total deferred tax expense from deferred rollforward data
        // Get the deferred rollforward data to extract DeferredExpense grand totals
        // IMPORTANT: In current provision view, `data` is filtered to CurrentActivityPL only,
        // but deferred rollforward requires all activity types.
        const { deferredRows } = createDeferredRollforwardData(deferredSourceData);

        uniqueEntities.forEach((entityCode) => {
          // Find the grand total row for this entity in the deferred rollforward data
          const grandTotalRow = deferredRows.find((row) =>
          row.AdjustmentName === 'GRAND TOTAL TEMPORARY DIFFERENCES' &&
          row.EntityCode === '' && // Grand total rows have empty EntityCode
          row.id === `grand-total-${entityCode}`
          );

          // Get the DeferredExpense value from the grand total row
          const deferredExpenseTotal = grandTotalRow?.DeferredExpense || 0;

          totalDeferredTaxExpense[entityCode] = deferredExpenseTotal;
        });

        // For ETR view, populate the TAX EXPENSE section instead of adding these as separate rows
        if (calculationType === 'effective-tax-rate' && taxExpenseSection) {
          // Recompute using only CurrentActivityPL rows (matching current provision filter) so
          // that ETR "Current Tax Expense" = current provision's TOTAL TAX EXPENSE exactly.
          const cpRows = data.filter((r) => r.ActivityType === 'CurrentActivityPL');
          const cpSections = ['Pretax', 'GS Permanent', 'GS Temporary', 'Permanent', 'Payable', 'Temporary', 'TI Permanent', 'TI Temporary', 'Net Operating Loss', 'Tax Effected Temporary', 'Tax Adj. Return', 'Tax Adj. Provision', 'Tax Adj. RTP'];
          const cpSectionTotals = {};
          cpSections.forEach((s) => {
            cpSectionTotals[s] = {};
            uniqueEntities.forEach((ec) => { cpSectionTotals[s][ec] = 0; });
          });
          cpRows.forEach((row) => {
            const section = row.AdjustmentSource || row.AdjustmentType;
            const ec = row.EntityCode;
            if (cpSectionTotals[section] && cpSectionTotals[section][ec] !== undefined) {
              const amount = selectedCurrency === 'USD' ? (row.USDInput || 0) : (row.LocalCurrencyInput || 0);
              cpSectionTotals[section][ec] += amount;
            }
          });

          uniqueEntities.forEach((entityCode) => {
            const deferredTaxExp = totalDeferredTaxExpense[entityCode] || 0;
            const rate = sectionTotalsCache['Current Tax Rate']?.[entityCode] || 0;

            // Standard TOTAL TAXABLE INCOME (raw values, no ETR tax-effecting of permanent sections)
            const cpTotalTaxableIncome =
              (cpSectionTotals['Pretax'][entityCode] || 0) +
              (cpSectionTotals['GS Permanent'][entityCode] || 0) +
              (cpSectionTotals['GS Temporary'][entityCode] || 0) +
              (cpSectionTotals['Permanent'][entityCode] || 0) +
              (cpSectionTotals['Payable'][entityCode] || 0) +
              (cpSectionTotals['Temporary'][entityCode] || 0) +
              (cpSectionTotals['TI Permanent'][entityCode] || 0) +
              (cpSectionTotals['TI Temporary'][entityCode] || 0) +
              (cpSectionTotals['Net Operating Loss'][entityCode] || 0);

            // CURRENT TAX EXPENSE BEFORE ADJ = totalTaxableIncome × rate (standard formula)
            const cpCurrentTaxExpBeforeAdj = cpTotalTaxableIncome * rate;

            // TOTAL RETURN BASIS = currentTaxExpBeforeAdj + taxEffectedTemp + returnBasisAdj
            const cpReturnBasisTotal = cpCurrentTaxExpBeforeAdj +
              (cpSectionTotals['Tax Effected Temporary'][entityCode] || 0) +
              (cpSectionTotals['Tax Adj. Return'][entityCode] || 0);

            // TOTAL CURRENT TAX EXPENSE = returnBasis + provisionBasisAdj + returnToProvisionAdj
            const cpTotalCurrentTaxExpense = cpReturnBasisTotal +
              (cpSectionTotals['Tax Adj. Provision'][entityCode] || 0) +
              (cpSectionTotals['Tax Adj. RTP'][entityCode] || 0);

            // Current Tax Expense (ETR top) = TOTAL CURRENT + TOTAL DEFERRED = current provision TOTAL TAX EXPENSE
            const provisionTotalTaxExpense = cpTotalCurrentTaxExpense + deferredTaxExp;

            taxExpenseSection.deferredTaxExpense[entityCode] = deferredTaxExp;
            taxExpenseSection.currentTaxExpense[entityCode] = provisionTotalTaxExpense;
            taxExpenseSection.totalTaxExpense[entityCode] = provisionTotalTaxExpense;
          });
        } else {
          // For current provision view, add the row as before
          pivotedRows.push(totalDeferredTaxExpense);
        }

        // Add total tax expense row (only for current provision, not ETR)
        if (calculationType !== 'effective-tax-rate') {
          const totalTaxExpense = {
            id: 'calculated-total-tax-expense',
            AdjustmentCode: '',
            AdjustmentName: 'TOTAL TAX EXPENSE',
            isCalculatedTotal: true
          };

          // Calculate: Total Current Tax Expense + Total Deferred Tax Expense
          uniqueEntities.forEach((entityCode) => {
            const currentTaxExpenseValue = totalCurrentTaxExpense[entityCode] || 0;
            const deferredTaxExpenseValue = totalDeferredTaxExpense[entityCode] || 0;

            totalTaxExpense[entityCode] = currentTaxExpenseValue + deferredTaxExpenseValue;
          });

          pivotedRows.push(totalTaxExpense);
        }
      }
    }

    // For ETR view, filter out specific sections while preserving calculations
    let finalPivotedRows = pivotedRows;
    if (calculationType === 'effective-tax-rate') {
      // Sections to exclude from ETR display (but keep their calculations in cache)
      const excludedSections = [
      'GAAP TO STAT TEMPORARY DIFFERENCES',
      'STAT TO TAX TEMPORARY DIFFERENCES',
      'TAXABLE INCOME TEMP ADJUSTMENT',
      'NET OPERATING LOSS',
      'TAX EFFECTED TEMPORARY DIFFERENCES'];


      // Track which section we're currently in to filter detail rows
      let currentSection = null;
      let inExcludedSection = false;

      finalPivotedRows = pivotedRows.filter((row) => {
        // Update current section when we hit a section header
        if (row.isSection) {
          currentSection = row.AdjustmentName;
          inExcludedSection = excludedSections.includes(currentSection);

          // Filter out excluded section headers
          if (inExcludedSection) {
            return false;
          }
          return true;
        }

        // Filter out specific calculated total rows
        if (row.isCalculatedTotal && row.AdjustmentName === 'TOTAL RETURN BASIS CURRENT TAX EXPENSE') {
          return false;
        }

        // Filter out section totals for excluded sections
        if (row.isSubFooter) {
          for (const excludedSection of excludedSections) {
            if (row.AdjustmentName.includes(excludedSection) ||
            row.AdjustmentName === `Total ${excludedSection}` ||
            row.AdjustmentName === `TOTAL ${excludedSection}`) {
              return false;
            }
          }
          // Reset section tracking after a total row
          inExcludedSection = false;
          return true;
        }

        // Filter out detail rows in excluded sections
        if (inExcludedSection) {
          return false;
        }

        return true;
      });

      // Insert the TAX EXPENSE section at the beginning
      if (taxExpenseSection) {
        finalPivotedRows.unshift(
          taxExpenseSection.header,
          taxExpenseSection.currentTaxExpense,
          taxExpenseSection.deferredTaxExpense,
          taxExpenseSection.totalTaxExpense
        );
      }
    }

    annotatePivotDrilldowns(finalPivotedRows);

    const deferredAdjustmentTypes = new Set([
      'GS Temporary',
      'Temporary',
      'TI Temporary',
      'Net Operating Loss',
      'Valuation Allowance',
      'Tax Effected Temporary',
      'Tax Effected Valuation Allowance'
    ]);
    const deferredLineageRows = deferredSourceData.filter((row) =>
    deferredAdjustmentTypes.has(row.AdjustmentSource || row.AdjustmentType)
    );
    finalPivotedRows.forEach((row) => {
      const name = String(row.AdjustmentName || '').toUpperCase();
      if (name.includes('DEFERRED TAX EXPENSE')) {
        attachDrilldownLineage(row, deferredLineageRows);
      } else if (name.includes('TOTAL TAX EXPENSE')) {
        attachDrilldownLineage(row, [...data, ...deferredLineageRows]);
      }
    });

    return { pivotedRows: finalPivotedRows, uniqueEntities };
  };

  // Filter and transform data using useMemo to prevent flickering
  const displayRows = useMemo(() => {
    let filteredRows = allRows;

    if (selectedScenario && selectedScenario.scenarioCode !== 'All') {
      filteredRows = filteredRows.filter((row) => row.ScenarioCode === selectedScenario.scenarioCode);
    }

    const entityArrTC = Array.isArray(selectedEntity) ? selectedEntity : [];
    const plainEntitiesTC = entityArrTC.filter((e) => !e.startsWith('[Group] '));
    if (plainEntitiesTC.length > 0) {
      filteredRows = filteredRows.filter((row) => plainEntitiesTC.includes(row.EntityCode));
    }

    if (selectedTag && selectedTag !== 'All') {
      filteredRows = filteredRows.filter((row) => row.Tag === selectedTag);
    }

    // For current provision table, only show adjustments with ActivityType = 'CurrentActivityPL'
    if (calculationType === 'current-provision') {
      filteredRows = filteredRows.filter((row) => row.ActivityType === 'CurrentActivityPL');
    }

    // Handle different calculation types
    let finalRows = [];

    if (calculationType === 'deferred-rollforward') {
      // Create deferred rollforward table structure
      const { deferredRows } = createDeferredRollforwardData(filteredRows);
      finalRows = deferredRows;
    } else {
      // Use filteredRows instead of allRows for pivot to ensure updates are reflected.
      // For current provision, keep the table filtered to CurrentActivityPL,
      // but compute deferred totals from the unfiltered scenario data.
      const deferredSourceRows = calculationType === 'current-provision' ?
      allRows.filter((row) => {
        if (selectedScenario && selectedScenario.scenarioCode !== 'All') {
          if (row.ScenarioCode !== selectedScenario.scenarioCode) return false;
        }
        const _entityArrDeferred = Array.isArray(selectedEntity) ? selectedEntity : [];
        const _plainEntitiesDeferred = _entityArrDeferred.filter((e) => !e.startsWith('[Group] '));
        if (_plainEntitiesDeferred.length > 0 && !_plainEntitiesDeferred.includes(row.EntityCode)) return false;
        if (selectedTag && selectedTag !== 'All') {
          if (row.Tag !== selectedTag) return false;
        }
        return true;
      }) :
      filteredRows;

      const { pivotedRows } = pivotData(filteredRows, deferredSourceRows);

      // ETR now shows all rows identical to current provision
      finalRows = pivotedRows;
    }

    return finalRows;
  }, [allRows, selectedScenario, selectedEntity, selectedTag, selectedCurrency, calculationType]);

  // Dialog handlers
  const handleOpenDialog = (row = defaultCalculation) => {
    setCurrentRow({ ...row });
    setEditMode(!!row.id);
    setOpenDialog(true);
  };


  // Handle cell clicks for editing adjustments - now opens full edit modal
  const handleCellClick = useCallback((params) => {
    if (params.row.isSection) return;
    if (!params.value || params.value === 0 || params.value === '-') return;

    const scenarioCode = selectedScenario?.scenarioCode;
    const lineageCodes = params.row.DrilldownAdjustmentCodes;
    const adjustmentCodes = lineageCodes?.length ? lineageCodes : [params.row.AdjustmentCode];
    const activeEntitySelections = Array.isArray(selectedEntity) ? selectedEntity : [];
    const selectedPlainEntities = activeEntitySelections.filter((value) => !value.startsWith('[Group] '));
    const selectedGroupEntities = activeEntitySelections
      .filter((value) => value.startsWith('[Group] '))
      .flatMap((value) => {
        const groupName = value.replace('[Group] ', '');
        const group = entityGroups.find((candidate) => candidate.name === groupName);
        return (group?.entityList || [])
          .map((entityId) => masterEntities.find((entity) => entity.id === entityId)?.EntityCode)
          .filter(Boolean);
      });
    const selectedEntityCodes = [...new Set([...selectedPlainEntities, ...selectedGroupEntities])];

    const isInActiveView = (row) => {
      if (scenarioCode && scenarioCode !== 'All' && row.ScenarioCode !== scenarioCode) return false;
      if (selectedTag && selectedTag !== 'All' && row.Tag !== selectedTag) return false;
      if (selectedEntityCodes.length && !selectedEntityCodes.includes(row.EntityCode)) return false;
      return true;
    };

    let matchingAdjustments;

    if (calculationType === 'deferred-rollforward') {
      const activityTypesToMatch = getDeferredActivityTypes(params.field, ACTIVITY_TYPES);
      if (!activityTypesToMatch) return;
      const lineageEntityCodes = params.row.DrilldownEntityCodes?.length ?
      params.row.DrilldownEntityCodes :
      [params.row.EntityCode].filter(Boolean);
      const lineageAdjustmentTypes = params.row.DrilldownAdjustmentTypes?.length ?
      params.row.DrilldownAdjustmentTypes :
      [params.row.AdjustmentType].filter(Boolean);

      matchingAdjustments = allRows.filter((row) => {
        const rowType = row.AdjustmentSource || row.AdjustmentType;
        return isInActiveView(row) &&
        adjustmentCodes.includes(row.AdjustmentCode) &&
        (!lineageEntityCodes.length || lineageEntityCodes.includes(row.EntityCode)) &&
        (!lineageAdjustmentTypes.length || lineageAdjustmentTypes.includes(rowType)) &&
        activityTypesToMatch.includes(row.ActivityType);
      });
    } else {
      const entityCodes = params.drilldownKeys?.length ? params.drilldownKeys : [params.field];
      const includesDeferredInputs = /DEFERRED|TOTAL TAX EXPENSE/i.test(params.row.AdjustmentName || '');
      matchingAdjustments = allRows.filter((row) =>
      isInActiveView(row) &&
      adjustmentCodes.includes(row.AdjustmentCode) &&
      entityCodes.includes(row.EntityCode) &&
      (calculationType !== 'current-provision' || includesDeferredInputs || row.ActivityType === 'CurrentActivityPL')
      );
    }

    if (matchingAdjustments && matchingAdjustments.length > 0) {
      // Always show table modal, even for single adjustment
      setMatchingAdjustments(matchingAdjustments);
      setSelectionDialogOpen(true);
    }
  }, [
  allRows,
  calculationType,
  entityGroups,
  masterEntities,
  selectedEntity,
  selectedScenario,
  selectedTag]
  );

  // Handle editing an adjustment - opens full modal like AdjustmentInputs page
  const handleEditAdjustment = (adjustment) => {
    setEditMode(true);
    const amount = adjustment.Amount || adjustment.Value || '';







    setCurrentAdjustment({
      id: adjustment.originalId || adjustment.id,
      AdjustmentId: adjustment.AdjustmentId,
      EntityCode: adjustment.EntityCode || '',
      EntityName: adjustment.EntityName || '',
      SubEntity: adjustment.SubEntity || '',
      AdjustmentCode: adjustment.AdjustmentCode || '',
      AdjustmentName: adjustment.AdjustmentName || '',
      ActivityType: adjustment.ActivityType || '',
      Amount: amount,
      USDAmount: adjustment.USDAmount || '',
      Tag: adjustment.Tag || '',
      SubTag: adjustment.SubTag || '',
      AdjustmentType: adjustment.AdjustmentType || '',
      ScenarioCode: adjustment.ScenarioCode || selectedScenario?.scenarioCode || 'SC001',
      PointInTime: adjustment.PointInTime || selectedScenario?.PointInTime || '',
      ModifiedBy: 'admin'
    });
    setEntityName(adjustment.EntityName || '');
    setAdjustmentName(adjustment.AdjustmentName || '');
    fetchPointInTimes();
    setOpenDialog(true);
  };

  // Handle deleting an adjustment from the selection dialog
  const handleDeleteAdjustment = async (adjustment) => {
    if (window.confirm('Are you sure you want to delete this adjustment?')) {
      try {
        const id = adjustment.originalId || adjustment.id;
        const adjustmentType = adjustment.AdjustmentType;
        const routePath = adjustmentTypeToRouteMap[adjustmentType];

        if (!routePath) {
          throw new Error('No route configured for this adjustment type');
        }

        await api.delete(`/adjustments/${routePath}/${id}`);

        setNotification({
          open: true,
          message: 'Adjustment deleted successfully',
          severity: 'success'
        });

        // Close the selection dialog and refresh data
        setSelectionDialogOpen(false);
        fetchTaxCalculations();
      } catch (error) {
        console.error('Error deleting adjustment:', error);
        setNotification({
          open: true,
          message: `Failed to delete adjustment: ${error.response?.data?.message || error.message}`,
          severity: 'error'
        });
      }
    }
  };

  // Fetch point in times for the dialog
  const fetchPointInTimes = async () => {
    try {
      const response = await api.get('/point-in-times');
      if (response.data && response.data.data) {
        setPointInTimes(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching point in times:', error);
    }
  };

  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const handleRequestClose = () => setConfirmCloseOpen(true);

  // Handle closing the dialog
  const handleClose = () => {
    setOpenDialog(false);
    setEditMode(false);
    setConfirmCloseOpen(false);
    setCurrentAdjustment({
      id: '',
      AdjustmentId: '',
      EntityCode: '',
      EntityName: '',
      SubEntity: '',
      AdjustmentCode: '',
      AdjustmentName: '',
      ActivityType: '',
      Amount: '',
      USDAmount: '',
      Tag: '',
      SubTag: '',
      AdjustmentType: '',
      ScenarioCode: '',
      PointInTime: '',
      ModifiedBy: 'admin'
    });
    setEntityName('');
    setAdjustmentName('');
  };

  // CRUD actions - Updated to work with full adjustment modal
  const handleSave = async () => {
    try {
      // Validate required fields
      if (!currentAdjustment.EntityCode || !currentAdjustment.AdjustmentCode) {
        setNotification({ open: true, message: 'Entity Code and Adjustment Code are required', severity: 'error' });
        return;
      }

      if (editMode) {
        // Handle full adjustment update


        const adjustmentType = currentAdjustment.AdjustmentType;
        const routePath = adjustmentTypeToRouteMap[adjustmentType];

        if (routePath && currentAdjustment.id) {
          // Prepare update data for backend
          const updateData = {
            EntityCode: currentAdjustment.EntityCode,
            AdjustmentCode: currentAdjustment.AdjustmentCode,
            Amount: parseFloat(currentAdjustment.Amount) || 0,
            USDAmount: parseFloat(currentAdjustment.USDAmount) || 0,
            ActivityType: currentAdjustment.ActivityType,
            Tag: currentAdjustment.Tag,
            SubTag: currentAdjustment.SubTag,
            SubEntity: currentAdjustment.SubEntity,
            ScenarioCode: currentAdjustment.ScenarioCode,
            PointInTime: currentAdjustment.PointInTime,
            ModifiedBy: currentAdjustment.ModifiedBy,
            AdjustmentId: currentAdjustment.AdjustmentId
          };



          // Make backend API call
          await api.put(`/adjustments/${routePath}/${currentAdjustment.id}`, updateData);

          // Refresh the calculations data
          await fetchTaxCalculations();

          setNotification({ open: true, message: 'Adjustment updated successfully', severity: 'success' });
        } else {
          setNotification({ open: true, message: 'Could not update adjustment - missing route or ID', severity: 'error' });
        }
      }

      handleClose();
    } catch (error) {
      console.error('Error saving adjustment:', error);
      setNotification({ open: true, message: `Failed to save adjustment: ${error.message}`, severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    try {
      setRows(rows.filter((row) => row.id !== id));
      setNotification({ open: true, message: 'Tax calculation deleted successfully', severity: 'success' });
    } catch (error) {
      setNotification({ open: true, message: 'Failed to delete tax calculation', severity: 'error' });
    }
  };

  // Remove the renderCell function as it's no longer needed with the new structure

  // Dynamic columns with entities as headers
  const columns = useMemo(() => {
    if (calculationType === 'deferred-rollforward') {
      // Create columns for deferred rollforward view
      const deferredColumns = [
      {
        field: 'AdjustmentName',
        headerName: 'Adjustment Name',
        minWidth: 400,
        maxWidth: 400,
        flex: 1,
        renderCell: (params) => {
          if (params.row.isSection) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#424242',
                color: 'white',
                padding: '8px',
                width: '100%',
                borderRadius: '4px'
              }}>
                  {params.formattedValue}
                </Box>);

          }
          if (params.row.isSubFooter) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '8px',
                width: '100%',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right'
              }}>
                  {params.formattedValue}
                </Box>);

          }
          // For regular adjustment rows, show EntityCode + AdjustmentCode + AdjustmentName
          return (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              gap: 1
            }}>
                <Box sx={{
                textAlign: 'left',
                fontWeight: 'bold',
                minWidth: 'fit-content',
                flexShrink: 0
              }}>
                  {params.row.EntityCode}
                </Box>
                <Box sx={{
                textAlign: 'left',
                fontWeight: 'bold',
                minWidth: 'fit-content',
                flexShrink: 0
              }}>
                  {params.row.AdjustmentCode}
                </Box>
                <Box sx={{
                textAlign: 'left',
                flex: 1
              }}>
                  {params.value}
                </Box>
              </Box>);

        }
      }];


      // Add regular activity type columns (first set)
      ACTIVITY_TYPES.forEach((activityType) => {
        let headerName;
        if (activityType === 'DeferredTrueUp') {
          headerName = 'DEFERRED TRUE UP (PRETAX)';
        } else if (activityType === 'AdjBeginningBalance') {
          headerName = 'ADJ. BEGINNING BALANCE (PRETAX)';
        } else if (activityType === 'ReturnToProvision') {
          headerName = 'RETURN TO PROVISION (PRETAX)';
        } else {
          headerName = `${activityType.replace(/([A-Z])/g, ' $1').trim().toUpperCase()} (PRETAX)`;
        }

        deferredColumns.push({
          field: activityType,
          headerName: headerName,
          minWidth: 120,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              // In effective tax rate view, make sub-footers clickable if they have values
              if (calculationType === 'effective-tax-rate' && params.value && params.value !== 0 && params.value !== '-') {
                return (
                  <Box sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    borderTop: '1px solid #ccc',
                    textAlign: 'right',
                    width: '100%',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => handleCellClick(params)}>

                    {params.formattedValue}
                  </Box>);

              }
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Make non-zero values clickable for editing
            if (params.value && params.value !== 0 && params.value !== '-') {
              return (
                <Box
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    textAlign: 'right',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => handleCellClick(params)}>

                  {params.formattedValue}
                </Box>);

            }
            return params.formattedValue;
          }
        });
      });

      // Add CTA Adjustment column (only show when currency is USD)
      if (selectedCurrency !== 'LC') {
        deferredColumns.push({
          field: 'CTAAdjustment',
          headerName: 'CTA ADJUSTMENT (PRETAX)',
          minWidth: 160,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Make non-zero values clickable for editing
            if (params.value && params.value !== 0 && params.value !== '-') {
              return (
                <Box
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    textAlign: 'right',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => handleCellClick(params)}>

                  {params.formattedValue}
                </Box>);

            }
            return params.formattedValue;
          }
        });
      }

      // Add Ending Balance column
      deferredColumns.push({
        field: 'EndingBalance',
        headerName: 'ENDING BALANCE (PRETAX)',
        minWidth: 160,
        type: 'number',
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === 0) return '-';
          return formatRoundedNumberWithParentheses(params.value);
        },
        renderCell: (params) => {
          // Don't show values for section headers
          if (params.row.isSection) {
            return null;
          }
          // Style sub-footer totals
          if (params.row.isSubFooter) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Style calculated totals
          if (params.row.isCalculatedTotal) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Make non-zero values clickable for editing
          if (params.value && params.value !== 0 && params.value !== '-') {
            return (
              <Box
                sx={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  textAlign: 'right',
                  width: '100%',
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }
                }}
                onClick={() => handleCellClick(params)}>

                {params.formattedValue}
              </Box>);

          }
          return params.formattedValue;
        }
      });

      // Add tax effected activity type columns (second set)
      ACTIVITY_TYPES.forEach((activityType) => {
        let headerName;
        if (activityType === 'AdjBeginningBalance') {
          headerName = 'ADJ. BEGINNING BALANCE (TAX EFFECTED)';
        } else {
          headerName = `${activityType.replace(/([A-Z])/g, ' $1').trim().toUpperCase()} (TAX EFFECTED)`;
        }

        deferredColumns.push({
          field: `${activityType}_TaxEffected`,
          headerName: headerName,
          minWidth: 140,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Make non-zero values clickable for editing (handleCellClick strips _TaxEffected suffix to resolve the ActivityType)
            if (params.value && params.value !== 0 && params.value !== '-') {
              return (
                <Box
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    textAlign: 'right',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => handleCellClick(params)}>

                  {params.formattedValue}
                </Box>);

            }
            // Regular values
            return params.formattedValue;
          }
        });
      });

      // Add Tax Rate Change Tax Effected column
      deferredColumns.push({
        field: 'TaxRateChange_TaxEffected',
        headerName: 'TAX RATE CHANGE (TAX EFFECTED)',
        minWidth: 180,
        type: 'number',
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === 0) return '-';
          return formatRoundedNumberWithParentheses(params.value);
        },
        renderCell: (params) => {
          // Don't show values for section headers
          if (params.row.isSection) {
            return null;
          }
          // Style sub-footer totals
          if (params.row.isSubFooter) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Style calculated totals
          if (params.row.isCalculatedTotal) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Regular values
          return params.formattedValue;
        }
      });

      // Add CTA Adjustment Tax Effected column (only show when currency is USD)
      if (selectedCurrency !== 'LC') {
        deferredColumns.push({
          field: 'CTAAdjustment_TaxEffected',
          headerName: 'CTA ADJUSTMENT (TAX EFFECTED)',
          minWidth: 180,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Regular values
            return params.formattedValue;
          }
        });
      }

      // Add Ending Balance Tax Effected column
      deferredColumns.push({
        field: 'EndingBalance_TaxEffected',
        headerName: 'ENDING BALANCE (TAX EFFECTED)',
        minWidth: 180,
        type: 'number',
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === 0) return '-';
          return formatRoundedNumberWithParentheses(params.value);
        },
        renderCell: (params) => {
          // Don't show values for section headers
          if (params.row.isSection) {
            return null;
          }
          // Style sub-footer totals
          if (params.row.isSubFooter) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Style calculated totals
          if (params.row.isCalculatedTotal) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Regular values
          return params.formattedValue;
        }
      });

      // Add new calculated columns
      const newColumns = [
      'DeferredExpense',
      'TaxRateChange',
      'ReturnToProvision',
      'DeferredTrueUpFinal',
      'CurrentActivity',
      'WriteOff',
      'ValuationAllowance'];


      newColumns.forEach((columnName) => {
        // Determine the header name - rollforward columns don't have (TAX EFFECTED) or (PRETAX) labels
        let headerName;
        let fieldName;
        if (columnName === 'DeferredTrueUpFinal') {
          headerName = 'DEFERRED TRUE UP';
          fieldName = 'DeferredTrueUpRollforward';
        } else if (columnName === 'ReturnToProvision') {
          headerName = 'RETURN TO PROVISION';
          fieldName = 'ReturnToProvisionRollforward';
        } else {
          headerName = columnName.replace(/([A-Z])/g, ' $1').trim().toUpperCase().replace(/TO/g, 'TO').replace(/UP/g, 'UP').replace(/OFF/g, 'OFF');
          fieldName = columnName;
        }

        deferredColumns.push({
          field: fieldName,
          headerName: headerName,
          minWidth: 160,
          type: 'number',
          valueFormatter: (params) => {
            if (params.value === null || params.value === undefined || params.value === 0) return '-';
            return formatRoundedNumberWithParentheses(params.value);
          },
          renderCell: (params) => {
            // Don't show values for section headers
            if (params.row.isSection) {
              return null;
            }
            // Style sub-footer totals
            if (params.row.isSubFooter) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Style calculated totals
            if (params.row.isCalculatedTotal) {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%'
                }}>
                  {params.formattedValue}
                </Box>);

            }
            // Make non-zero values clickable for editing
            if (params.value && params.value !== 0 && params.value !== '-') {
              return (
                <Box
                  sx={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    textAlign: 'right',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => handleCellClick(params)}>

                  {params.formattedValue}
                </Box>);

            }
            return params.formattedValue;
          }
        });
      });

      return enableTotalCellDrilldown(deferredColumns, handleCellClick);
    }

    if (allRows.length === 0) {
      return [
      { field: 'AdjustmentCode', headerName: 'Adjustment Code', minWidth: 140 },
      { field: 'AdjustmentName', headerName: 'Adjustment Name', width: 200 }];

    }

    // Get unique entities from filtered data based on selected scenario, entity, and tag
    let filteredRows = allRows;
    if (selectedScenario && selectedScenario.scenarioCode !== 'All') {
      filteredRows = filteredRows.filter((row) => row.ScenarioCode === selectedScenario.scenarioCode);
    }

    const entityArrColsTC = Array.isArray(selectedEntity) ? selectedEntity : [];
    const plainEntitiesColsTC = entityArrColsTC.filter((e) => !e.startsWith('[Group] '));
    const groupSelectionsColsTC = entityArrColsTC.filter((e) => e.startsWith('[Group] '));
    if (plainEntitiesColsTC.length > 0) {
      filteredRows = filteredRows.filter((row) => plainEntitiesColsTC.includes(row.EntityCode));
    }

    if (selectedTag && selectedTag !== 'All') {
      filteredRows = filteredRows.filter((row) => row.Tag === selectedTag);
    }

    // For current provision, use all entities from the selected scenario's entityList
    let uniqueEntities;
    if (calculationType === 'current-provision' && selectedScenario?.entityList && selectedScenario.entityList.length > 0) {
      const entitiesWithData = [...new Set(filteredRows.map((row) => row.EntityCode))].filter((code) => code);
      const entitiesWithoutData = selectedScenario.entityList.filter((code) => !entitiesWithData.includes(code));
      uniqueEntities = [...entitiesWithData.sort(), ...entitiesWithoutData.sort()];
    } else {
      uniqueEntities = [...new Set(filteredRows.map((row) => row.EntityCode))].
      filter((code) => code).
      sort();
    }

    // Apply entity filter to columns
    if (entityArrColsTC.length > 0) {
      let allowedCodesTC = [...plainEntitiesColsTC];
      if (groupSelectionsColsTC.length > 0) {
        groupSelectionsColsTC.forEach((sel) => {
          const groupName = sel.replace('[Group] ', '');
          const selectedGroup = entityGroups.find((g) => g.name === groupName);
          if (selectedGroup?.entityList?.length > 0) {
            const groupEntityCodes = selectedGroup.entityList.
            map((entityId) => masterEntities.find((e) => e.id === entityId)?.EntityCode).
            filter((code) => code);
            allowedCodesTC = [...allowedCodesTC, ...groupEntityCodes];
          }
        });
      }
      if (allowedCodesTC.length > 0) {
        uniqueEntities = uniqueEntities.filter((code) => allowedCodesTC.includes(code));
      }
    }

    // Create base columns for adjustment info
    const baseColumns = [
    {
      field: 'AdjustmentName',
      headerName: 'Adjustment Name',
      minWidth: 400,
      maxWidth: 400,
      flex: 1,
      renderCell: (params) => {
        if (params.row.isSection) {
          return (
            <Box sx={{
              fontWeight: 'bold',
              backgroundColor: '#424242',
              color: 'white',
              padding: '8px',
              width: '100%',
              borderRadius: '4px'
            }}>
              {params.formattedValue}
            </Box>);

        }
        if (params.row.isSubFooter) {
          return (
            <Box sx={{
              fontWeight: 'bold',
              backgroundColor: '#f5f5f5',
              padding: '8px',
              width: '100%',
              borderRadius: '4px',
              borderTop: '1px solid #ccc',
              textAlign: 'right'
            }}>
              {params.formattedValue}
            </Box>);

        }
        if (params.row.isCalculatedTotal) {
          return (
            <Box sx={{
              fontWeight: 'bold',
              backgroundColor: '#f5f5f5',
              padding: '8px',
              width: '100%',
              borderRadius: '4px',
              borderTop: '1px solid #ccc',
              textAlign: params.row.AdjustmentName === 'CURRENT TAX RATE PERCENTAGE' ? 'left' : 'right'
            }}>
              {params.formattedValue}
            </Box>);

        }

        // For regular adjustment rows in current provision mode, show code + name with custom layout
        if (calculationType === 'current-provision' && params.row.AdjustmentCode) {
          return (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              gap: 1
            }}>
              <Box sx={{
                textAlign: 'left',
                fontWeight: 'bold',
                minWidth: 'fit-content',
                flexShrink: 0
              }}>
                {params.row.AdjustmentCode}
              </Box>
              <Box sx={{
                textAlign: 'left',
                flex: 1
              }}>
                {params.value}
              </Box>
            </Box>);

        }

        return params.value;
      }
    }];

    // Add Total Consolidated column before entity columns (only if there are multiple entities)
    if (uniqueEntities.length > 1) {
      baseColumns.push({
        field: 'TotalConsolidated',
        drilldownKeys: uniqueEntities,
        headerName: 'Total Consolidated',
        minWidth: 180,
        type: 'number',
        valueGetter: (params) => {
          // Don't calculate for section headers
          if (params.row.isSection) {
            return null;
          }

          // Calculate total of all entity values for this row
          let total = 0;
          uniqueEntities.forEach((entityCode) => {
            const entityValue = params.row[entityCode];
            if (entityValue && typeof entityValue === 'number') {
              total += entityValue;
            }
          });

          return total === 0 ? null : total;
        },
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined) return '-';

          // Special formatting for Current Tax Rate row - already formatted as string
          if (params.row && params.row.AdjustmentName === 'CURRENT TAX RATE PERCENTAGE') {
            // If it's already a string with %, return as is
            if (typeof params.value === 'string' && params.value.includes('%')) {
              return params.value;
            }
            // Otherwise format it
            const numValue = parseFloat(params.value) || 0;
            return `${numValue.toFixed(2)}%`;
          }

          if (params.value === 0) return '-';

          return formatRoundedNumberWithParentheses(params.value);
        },
        renderCell: (params) => {
          // Don't show values for section headers
          if (params.row.isSection) {
            return null;
          }
          // Style sub-footer totals
          if (params.row.isSubFooter) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
              {params.formattedValue}
            </Box>);

          }
          // Style calculated totals
          if (params.row.isCalculatedTotal) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: params.row.AdjustmentName === 'CURRENT TAX RATE PERCENTAGE' ? 'left' : 'right',
                width: '100%'
              }}>
              {params.formattedValue}
            </Box>);

          }
          // Regular cells - not clickable for Total Consolidated column
          return (
            <Box sx={{
              padding: '4px 8px',
              borderRadius: '4px',
              textAlign: 'right',
              width: '100%'
            }}>
            {params.formattedValue}
          </Box>);

        }
      });
    }

    // Add entity columns
    uniqueEntities.forEach((entityCode) => {
      const entity = masterEntities.find((e) => e.EntityCode === entityCode);
      const entityName = entity?.EntityName || entityCode;

      baseColumns.push({
        field: entityCode,
        drilldownKeys: [entityCode],
        headerName: `${entityName} (${entityCode})`,
        minWidth: 150,
        type: 'number',
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined) return '-';

          // Special formatting for Current Tax Rate row - already formatted as string
          if (params.row && params.row.AdjustmentName === 'CURRENT TAX RATE PERCENTAGE') {
            // If it's already a string with %, return as is
            if (typeof params.value === 'string' && params.value.includes('%')) {
              return params.value;
            }
            // Otherwise format it
            const numValue = parseFloat(params.value) || 0;
            return `${numValue.toFixed(2)}%`;
          }

          if (params.value === 0) return '-';

          return formatRoundedNumberWithParentheses(params.value);
        },
        renderCell: (params) => {
          // Don't show values for section headers
          if (params.row.isSection) {
            return null;
          }
          // Style sub-footer totals
          if (params.row.isSubFooter) {
            // In effective tax rate view, make sub-footers clickable if they have values
            if (calculationType === 'effective-tax-rate' && params.value && params.value !== 0 && params.value !== '-') {
              return (
                <Box sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  borderTop: '1px solid #ccc',
                  textAlign: 'right',
                  width: '100%',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }
                }}
                onClick={() => handleCellClick(params)}>

                  {params.formattedValue}
                </Box>);

            }
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Style calculated totals
          if (params.row.isCalculatedTotal) {
            return (
              <Box sx={{
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                padding: '4px 8px',
                borderRadius: '4px',
                borderTop: '1px solid #ccc',
                textAlign: params.row.AdjustmentName === 'CURRENT TAX RATE PERCENTAGE' ? 'left' : 'right',
                width: '100%'
              }}>
                {params.formattedValue}
              </Box>);

          }
          // Make non-zero values clickable for editing
          if (params.value && params.value !== 0 && params.value !== '-') {
            return (
              <Box
                sx={{
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  textAlign: 'right',
                  width: '100%',
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }
                }}
                onClick={() => handleCellClick(params)}>

                {params.formattedValue}
              </Box>);

          }
          return params.formattedValue;
        }
      });
    });



    return enableTotalCellDrilldown(baseColumns, handleCellClick);
  }, [allRows, masterEntities, entityGroups, selectedCurrency, calculationType, selectedScenario, selectedEntity, selectedTag, handleCellClick]);

  return (
    <div className="entity-inputs-container">
      <div className="main-content">
        <div className="toolbar-container">
          <Typography className="page-title" variant="h6">Tax Calculations</Typography>
          <div className="button-group">
            {/* Export 
              <Button
               variant="outlined"
               size="small"
               startIcon={<DownloadIcon />}
               className="action-button"
               onClick={exportToExcel}
               disabled={rows.length === 0}
              >
               Export
              </Button>
              */}
          </div>
        </div>
        <div className="table-container">
          {loading && <LinearProgress />}
          <EnhancedDataTable
            rows={displayRows}
            columns={columns}
            loading={loading}
            tableName="taxCalculations"
            height="100%"
            className="custom-datagrid"
            disableColumnSorting={true}
            pinnedColumns={{ right: ['actions'], left: ['AdjustmentName'] }}
            getRowClassName={(params) => {
              return params.row.isSection ? 'section-header' : '';
            }} />

        </div>
      </div>
      
      {/* Edit Adjustment Dialog */}
      <UnsavedChangesDialog
        open={confirmCloseOpen}
        onConfirm={handleClose}
        onCancel={() => setConfirmCloseOpen(false)} />

      <Dialog
        open={openDialog}
        onClose={handleRequestClose}
        maxWidth="md"
        fullWidth>

        <DialogTitle>{editMode ? 'Edit Adjustment Input' : 'Add New Adjustment Input'}</DialogTitle>
        <DialogContent className="dialog-content">
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={[...masterEntities].sort((a, b) => (a.EntityCode || '').localeCompare(b.EntityCode || ''))}
                getOptionLabel={(option) => option.EntityCode || ''}
                loading={entityLoading}
                value={masterEntities.find((e) => e.EntityCode === currentAdjustment.EntityCode) || null}
                onChange={(event, newValue) => {
                  setCurrentAdjustment({
                    ...currentAdjustment,
                    EntityCode: newValue?.EntityCode || '',
                    EntityName: newValue?.EntityName || ''
                  });
                  setEntityName(newValue?.EntityName || '');
                }}
                renderInput={(params) =>
                <TextField
                  {...params}
                  label="Entity Code"
                  required
                  autoComplete="off" />

                } />

            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Entity Name"
                fullWidth
                value={entityName}
                InputProps={{ readOnly: true }}
                autoComplete="off"
                disabled />

            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sub Entity"
                fullWidth
                value={currentAdjustment.SubEntity}
                onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, SubEntity: e.target.value })}
                autoComplete="off" />

            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={[...masterAdjustments].sort((a, b) => (a.AdjustmentCode || '').localeCompare(b.AdjustmentCode || ''))}
                getOptionLabel={(option) => option.AdjustmentCode || ''}
                loading={adjustmentLoading}
                value={masterAdjustments.find((a) => a.AdjustmentCode === currentAdjustment.AdjustmentCode) || null}
                onChange={(event, newValue) => {
                  setCurrentAdjustment({
                    ...currentAdjustment,
                    AdjustmentCode: newValue?.AdjustmentCode || '',
                    AdjustmentName: newValue?.AdjustmentName || ''
                  });
                  setAdjustmentName(newValue?.AdjustmentName || '');
                }}
                renderInput={(params) =>
                <TextField
                  {...params}
                  label="Adjustment Code"
                  required
                  autoComplete="off" />

                } />

            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Adjustment Name"
                fullWidth
                value={adjustmentName}
                InputProps={{ readOnly: true }}
                disabled
                autoComplete="off" />

            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Activity Type</InputLabel>
                <Select
                  value={currentAdjustment.ActivityType}
                  label="Activity Type"
                  onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, ActivityType: e.target.value })}>

                  {getActivityTypeOptions(currentAdjustment.AdjustmentType).map((option) =>
                  <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Local Currency Amount"
                type="text"
                fullWidth
                autoComplete="off"
                value={formatNumberWithCommas(currentAdjustment.Amount)}
                onChange={(e) => handleAmountChange(e.target.value)}
                required
                helperText={(() => {
                  const fxRate = getFXRateForEntity(currentAdjustment.EntityCode, currentAdjustment.ActivityType);
                  const isPretax = currentAdjustment.AdjustmentType === 'Pretax' || currentAdjustment.AdjustmentType === 'PretaxAdjustment';
                  if (isPretax) {
                    return 'Pretax adjustments: Manual entry only (no auto-conversion)';
                  }
                  return fxRate ? `FX Rate: ${fxRate.toFixed(6)} (USD auto-calculated)` : 'Select entity to enable auto-conversion';
                })()} />

            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="USD Amount"
                type="text"
                fullWidth
                autoComplete="off"
                value={formatNumberWithCommas(currentAdjustment.USDAmount)}
                onChange={(e) => handleUSDAmountChange(e.target.value)}
                helperText={(() => {
                  const fxRate = getFXRateForEntity(currentAdjustment.EntityCode, currentAdjustment.ActivityType);
                  const isPretax = currentAdjustment.AdjustmentType === 'Pretax' || currentAdjustment.AdjustmentType === 'PretaxAdjustment';
                  if (isPretax) {
                    return 'Pretax adjustments: Manual entry only (no auto-conversion)';
                  }
                  return fxRate ? `FX Rate: ${fxRate.toFixed(6)} (Local auto-calculated)` : 'Select entity to enable auto-conversion';
                })()} />

            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="tag-label">Tag</InputLabel>
                <Select
                  labelId="tag-label"
                  value={currentAdjustment.Tag || ''}
                  label="Tag"
                  onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, Tag: e.target.value })}>

                  {/* Show current value if it exists but isn't in options */}
                  {currentAdjustment.Tag && !TAG_OPTIONS.includes(currentAdjustment.Tag) &&
                  <MenuItem key={currentAdjustment.Tag} value={currentAdjustment.Tag}>
                      {currentAdjustment.Tag} (Current)
                    </MenuItem>
                  }
                  {TAG_OPTIONS.map((option) =>
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="subtag-label">Sub Tag</InputLabel>
                <Select
                  labelId="subtag-label"
                  value={currentAdjustment.SubTag || ''}
                  label="Sub Tag"
                  onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, SubTag: e.target.value })}>

                  {/* Show current value if it exists but isn't in options */}
                  {currentAdjustment.SubTag && !SUBTAG_OPTIONS.includes(currentAdjustment.SubTag) &&
                  <MenuItem key={currentAdjustment.SubTag} value={currentAdjustment.SubTag}>
                      {currentAdjustment.SubTag} (Current)
                    </MenuItem>
                  }
                  {SUBTAG_OPTIONS.map((option) =>
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Point In Time</InputLabel>
                <Select
                  value={currentAdjustment.PointInTime}
                  label="Point In Time"
                  onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, PointInTime: e.target.value })}>

                  {pointInTimes.map((pit) =>
                  <MenuItem key={pit.PointInTime} value={pit.PointInTime}>
                      {pit.PointInTimeName}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Scenario Code"
                fullWidth
                value={currentAdjustment.ScenarioCode}
                autoComplete="off"
                InputProps={{ readOnly: true }}
                disabled />

            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!currentAdjustment.EntityCode || !currentAdjustment.AdjustmentCode}>

            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Selection Dialog for Multiple Adjustments */}
      <Dialog
        open={selectionDialogOpen}
        onClose={() => setSelectionDialogOpen(false)}
        maxWidth="xl"
        fullWidth>

        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
          Select Adjustment to Edit or Delete
          <IconButton
            onClick={() => setSelectionDialogOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}>

            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Multiple adjustments exist for this cell. Click on a row to edit, or use the action buttons:
          </Typography>
          <Box sx={{ height: 500, width: '100%' }}>
            <SearchableDataTable
              searchPlaceholder="Search adjustments..."
              rows={matchingAdjustments.map((adj, index) => ({
                ...adj,
                id: adj.id || `adj-${index}`
              }))}
              columns={getSelectionDialogColumns(
                (row) => {
                  setSelectionDialogOpen(false);
                  handleEditAdjustment(row);
                },
                (row) => {
                  handleDeleteAdjustment(row);
                }
              )}
              loading={false}
              pageSize={10}
              tableName="adjustment-selection-dialog"
              height="100%"
              onRowClick={(params) => {
                setSelectionDialogOpen(false);
                handleEditAdjustment(params.row);
              }} />

          </Box>
        </DialogContent>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar open={notification.open} autoHideDuration={4000} onClose={() => setNotification({ ...notification, open: false })}>
        <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </div>);

};

export default TaxCalculations;
